// src/pages/Dashboard/Dashboard.jsx

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Table, Tag, Button, Spin, Alert, Tooltip, Badge, Avatar, Modal, Input, message as antMsg } from 'antd';
import {
  TeamOutlined, CheckCircleOutlined, WarningOutlined,
  ClockCircleOutlined, ReloadOutlined, PlusOutlined,
  UploadOutlined, EyeOutlined, FireOutlined, TrophyOutlined,
  ThunderboltOutlined, UserDeleteOutlined, PhoneOutlined,
  ArrowRightOutlined, SyncOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LabelList, AreaChart, Area,
} from 'recharts';
import api from '../../services/api';
import dashboardService from '../../services/dashboardService';
import CohortDay10Summary from '../Cohort/CohortDay10Summary';
import FeedbackQuestionCards from './FeedbackQuestionCards';
import employeeService  from '../../services/employeeService';
import clusterService   from '../../services/clusterService';
import dayjs from 'dayjs';

// ── Design tokens ────────────────────────────────────────────────
const C = {
  navy:'#1B2D6B', blue:'#1E6FBF', lblue:'#EAF1F8',
  green:'#16a34a', lgreen:'#F0FDF4',
  red:'#DC2626',   lred:'#FEF2F2',
  amber:'#B45309', lamber:'#FFFBEB',
  purple:'#7c3aed',lpurple:'#f5f3ff',
  teal:'#0891b2',  lteal:'#ecfeff',
  text1:'#1a2d4a', text2:'#4a6580', text3:'#7a96b0',
  border:'#DCE6F0', surf:'#F5F8FC', white:'#FFFFFF',
};

const CALL_KEYS   = ['day10','day30','day60','day90','day120','day150','day180'];
const CALL_LABELS = { day10:'Day 10', day30:'Day 30', day60:'Day 60', day90:'Day 90', day120:'Day 120', day150:'Day 150', day180:'Day 180' };
const CALL_SHORT  = { day10:'D10', day30:'D30', day60:'D60', day90:'D90', day120:'D120', day150:'D150', day180:'D180' };
const CALL_DAYS   = { day10:10, day30:30, day60:60, day90:90, day120:120, day150:150, day180:180 };

const HEALTH_CFG = {
  'Completed':   { color:C.green,  bg:'#dcfce7', label:'Completed',   emoji:'✅' },
  'On Track':    { color:C.blue,   bg:'#dbeafe', label:'On Track',    emoji:'🔵' },
  'In Progress': { color:C.purple, bg:'#ede9fe', label:'In Progress', emoji:'🔄' },
  'At Risk':     { color:C.amber,  bg:'#fef3c7', label:'At Risk',     emoji:'⚠️'  },
  'Not Started': { color:C.text3,  bg:'#f1f5f9', label:'Not Started', emoji:'⬜' },
  'Resigned':    { color:C.red,    bg:'#fee2e2', label:'Resigned',    emoji:'🚪' },
};

const HEALTH_ORDER = ['Completed','On Track','In Progress','At Risk','Not Started'];

// ── Journey cutoff — DOJ before 01-Dec-2025 = Legacy (completed for record) ──
// Falls back to cohortDate when dateOfJoining is missing (e.g. CTP Nov-2025 campus batch)
const JOURNEY_CUTOFF = '2025-12-01';
const isLegacy = (emp) => {
  const doj = emp?.dateOfJoining || emp?.cohortDate;
  return doj && dayjs(doj).isBefore(dayjs(JOURNEY_CUTOFF));
};

// ── Pure helpers ─────────────────────────────────────────────────
const getJourneyProgress = emp => {
  if (isLegacy(emp)) return 100; // Legacy employees = journey 100% complete
  const done = CALL_KEYS.filter(k=>emp.calls?.find(c=>c.callKey===k&&c.status==='Completed')).length;
  const naCount = CALL_KEYS.filter(k=>emp.calls?.find(c=>c.callKey===k&&c.status==='NA')).length;
  const applicable = Math.max(CALL_KEYS.length-naCount,1);
  return Math.round((done/applicable)*100);
};
const getAgingDays = emp => {
  const doj = emp?.dateOfJoining || emp?.cohortDate;
  return doj ? dayjs().diff(dayjs(doj), 'day') : 0;
};

const getJourneyHealth = emp => {
  if (isLegacy(emp)) return 'Completed'; // Legacy = journey completed (for record)
  if (!emp.dateOfJoining) return 'Not Started';
  if (emp.isResigned)     return 'Resigned';
  const compl = emp.calls?.filter(c=>c.status==='Completed').length ?? 0;
  if (compl===CALL_KEYS.length) return 'Completed';
  if (emp.calls?.some(c=>c.status==='No Response' && c.status!=='NA')) return 'At Risk';
  if (compl>0) return 'On Track';
  return CALL_KEYS.some(k=>dayjs(emp.dateOfJoining).add(CALL_DAYS[k],'day').diff(dayjs(),'day')<=0)
    ? 'In Progress' : 'Not Started';
};

const avatarColor = (name='') => {
  const cols=[C.navy,C.blue,C.green,C.amber,'#7c3aed','#0891b2','#be185d'];
  let h=0; for(const ch of name) h=(h*31+ch.charCodeAt(0))%cols.length;
  return cols[h];
};

// ── Reusable UI atoms ───────────────────────────────────────────
const Bar_ = ({pct=0,color=C.blue,h=6,showLabel=true}) => (
  <div style={{display:'flex',alignItems:'center',gap:6}}>
    <div style={{flex:1,height:h,borderRadius:99,background:'#e2e8f0',overflow:'hidden'}}>
      <div style={{width:`${Math.min(pct,100)}%`,height:'100%',
        background:color,borderRadius:99,transition:'width .4s'}}/>
    </div>
    {showLabel&&<span style={{fontSize:11,fontWeight:700,color,minWidth:30}}>{pct}%</span>}
  </div>
);

const Chip = ({label,val,color,bg}) => (
  <span style={{display:'inline-flex',alignItems:'center',gap:4,
    padding:'2px 8px',borderRadius:20,fontSize:11,fontWeight:700,
    color,background:bg,border:`1px solid ${color}33`}}>
    {label}: {val}
  </span>
);

const ChartTip = ({active,payload,label}) => {
  if(!active||!payload?.length) return null;
  return (
    <div style={{background:C.white,border:`1px solid ${C.border}`,
      borderRadius:10,padding:'10px 14px',
      boxShadow:'0 8px 24px rgba(0,0,0,0.1)',fontSize:12}}>
      <div style={{fontWeight:700,color:C.text1,marginBottom:6}}>{label}</div>
      {payload.map(p=>(
        <div key={p.name} style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
          <span style={{width:8,height:8,borderRadius:2,
            background:p.color,display:'inline-block',flexShrink:0}}/>
          <span style={{color:C.text2}}>{p.name}:</span>
          <span style={{fontWeight:700,color:C.text1}}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ── KPI card ─────────────────────────────────────────────────────
const KpiCard = ({icon,label,value,sub,note,color,pct,loading}) => (
  <div style={{
    background:C.white,borderRadius:14,padding:'18px 20px',height:'100%',
    border:`1.5px solid ${color}28`,
    boxShadow:`0 1px 8px ${color}12,0 4px 16px rgba(0,0,0,0.04)`,
    display:'flex',flexDirection:'column',gap:10,
  }}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
      <div style={{fontSize:11,fontWeight:700,color:C.text3,
        letterSpacing:'0.8px',textTransform:'uppercase'}}>{label}</div>
      <div style={{width:38,height:38,borderRadius:10,flexShrink:0,
        background:`${color}15`,display:'flex',alignItems:'center',
        justifyContent:'center',fontSize:18,color}}>{icon}</div>
    </div>
    {loading
      ? <Spin size="small"/>
      : <div style={{fontSize:32,fontWeight:800,color,lineHeight:1,
          letterSpacing:'-1px'}}>{value??'—'}</div>
    }
    {sub&&<div style={{fontSize:11,color:C.text3}}>{sub}</div>}
    {note&&<div style={{fontSize:11,fontWeight:700,color}}>{note}</div>}
    {pct!=null&&<Bar_ pct={pct} color={color} h={5}/>}
  </div>
);

// ── Section header ───────────────────────────────────────────────
const Sec = ({title,sub,action}) => (
  <div style={{display:'flex',justifyContent:'space-between',
    alignItems:'flex-end',marginBottom:16}}>
    <div>
      <div style={{fontSize:15,fontWeight:700,color:C.text1,
        display:'flex',alignItems:'center',gap:8}}>{title}</div>
      {sub&&<div style={{fontSize:12,color:C.text3,marginTop:2}}>{sub}</div>}
    </div>
    {action}
  </div>
);

// ── Panel wrapper ────────────────────────────────────────────────
const Panel = ({children,style={}}) => (
  <div style={{background:C.white,borderRadius:14,padding:'20px 24px',
    border:`1px solid ${C.border}`,
    boxShadow:'0 1px 4px rgba(0,0,0,0.04)',...style}}>
    {children}
  </div>
);

// ════════════════════════════════════════════════════════════════
export default function Dashboard() {
  const nav = useNavigate();
  const [loading,   setLoading]   = useState(true);
  const [stats,     setStats]     = useState(null);
  const [employees, setEmployees] = useState([]);
  const [atRisk,    setAtRisk]    = useState([]);
  const [error,     setError]     = useState('');
  const [compliance,  setCompliance]  = useState([]);
  const [compLoading, setCompLoading] = useState(false);
  const [compSearch,  setCompSearch]  = useState('');
  const [compPage,    setCompPage]    = useState(1);
  const [attrStats,   setAttrStats]   = useState(null);
  const [attrLoading, setAttrLoading] = useState(false);
  const [inboundStats,   setInboundStats]   = useState(null);
  const [inboundLoading, setInboundLoading] = useState(false);
  const [showInfoCols,      setShowInfoCols]      = useState(false);
  const [showBreakdownCols, setShowBreakdownCols] = useState(false);
  const [showHistorical,    setShowHistorical]    = useState(false);
  const [show180Modal,      setShow180Modal]      = useState(false);
  const [drillDown,         setDrillDown]         = useState(null);
  const [compDrill,         setCompDrill]         = useState(null);
  const [compDrillSearch,   setCompDrillSearch]   = useState('');
  const [compDrillSort,     setCompDrillSort]     = useState({ key: null, dir: 'asc' });
  const [modal180Search, setModal180Search] = useState('');
  const [syncLoading,    setSyncLoading]    = useState(false);
  const [clusterConfig,  setClusterConfig]  = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [sRes,eRes,rRes] = await Promise.all([
        dashboardService.getStats(),
        employeeService.getAll({page:1,limit:2000}),
        dashboardService.getAtRisk(),
      ]);
      setStats(sRes.data);
      setEmployees(eRes.data?.data||[]);
      setAtRisk(rRes.data||[]);
    } catch(e){ setError(e.message||'Failed to load'); }
    finally    { setLoading(false); }
  },[]);

  useEffect(()=>{ loadAll(); },[loadAll]);

  const handleSharepointSync = async () => {
    setSyncLoading(true);
    try {
      const res = await api.post("/sharepoint-sync/sync-attrition", {}, { timeout: 180000 });
      const r = res.data;
      antMsg.success(
        `Sync done — ${r.inserted} records, ${r.employeesUpdated} employees updated` +
        (r.staleReset ? `, ${r.staleReset} stale flags cleared` : ''),
        6,
      );
      loadAll();
    } catch (e) {
      antMsg.error("SharePoint sync failed: " + (e?.response?.data?.message || e?.message || "Unknown error"));
    } finally {
      setSyncLoading(false);
    }
  };

  const loadCompliance = useCallback(async () => {
    setCompLoading(true);
    try {
      const res = await dashboardService.getCompliance();
      const rows = Array.isArray(res.data) ? res.data
        : Array.isArray(res.data?.data) ? res.data.data : [];
      setCompliance(rows);
    } catch { setCompliance([]); }
    finally { setCompLoading(false); }
  }, []);
  useEffect(() => { loadCompliance(); }, [loadCompliance]);

  const loadAttrStats = useCallback(async () => {
    setAttrLoading(true);
    try {
      const res = await api.get('/employees/attrition-stats');
      const payload = res.data?.data ?? res.data;
      setAttrStats(payload);
    } catch { setAttrStats(null); }
    finally { setAttrLoading(false); }
  }, []);
  useEffect(() => { loadAttrStats(); }, [loadAttrStats]);

  const loadInbound = useCallback(async () => {
    setInboundLoading(true);
    try {
      const res = await api.get('/inbound/summary');
      setInboundStats(res.data?.data ?? res.data);
    } catch { setInboundStats(null); }
    finally { setInboundLoading(false); }
  }, []);
  useEffect(() => { loadInbound(); }, [loadInbound]);

  const loadClusterConfig = useCallback(async () => {
    try {
      const [summaryRes, aliasRes] = await Promise.all([
        clusterService.getSummary(),
        clusterService.getAliases(),
      ]);
      const clusters = Array.isArray(summaryRes) ? summaryRes : (summaryRes?.data || []);
      const aliases  = Array.isArray(aliasRes)   ? aliasRes  : (aliasRes?.data  || []);
      setClusterConfig({ clusters, aliases });
    } catch { setClusterConfig(null); }
  }, []);
  useEffect(() => { loadClusterConfig(); }, [loadClusterConfig]);

  // ── All computed analytics ────────────────────────────────────
  const a = useMemo(()=>{
    if(!employees.length) return null;
    const active   = employees.filter(e=>!e.isResigned);
    const resigned = employees.filter(e=> e.isResigned);

    // ── Global call counts — computed from employee data (legacy excluded) ──
    // Backend callSummary includes pre-Dec'25 legacy employees; employee-level
    // computation keeps all four KPIs on the same population as "Total Targeted".
    const allCounts = { completed:0, pending:0, noResponse:0 };
    active.forEach(e => {
      if (isLegacy(e)) return;
      CALL_KEYS.forEach(k => {
        if (CALL_DAYS[k] > getAgingDays(e)) return; // not due yet
        const call = e.calls?.find(c => c.callKey === k);
        if (!call) { allCounts.pending++;   return; }
        if (call.status === 'Completed')   { allCounts.completed++;   return; }
        if (call.status === 'No Response') { allCounts.noResponse++;  return; }
        if (call.status === 'NA')          return; // not applicable — skip
        allCounts.pending++; // Call Later, blank, or other status
      });
    });

    // Employee-level pending count — matches EmployeeList "Pending Calls" tab
    // Rules: pre-Dec'25 always excluded; journey-completed Dec'25+ (≥180d) check raw calls;
    // active Dec'25+ count only Missed or unlogged calls 1+ days past due
    const pendingEmpsCount = active.filter(e => {
      if (isLegacy(e)) return false; // pre-Dec'25 (Oct/Nov'25 etc.) — always excluded first
      const doj = e.dateOfJoining || e.cohortDate;
      if (!doj) return false;
      const agingDays = dayjs().diff(dayjs(doj), 'day');
      if (agingDays >= 180) {
        // Journey-completed Dec'25+: only flag explicitly Missed calls
        // Unlogged calls = journey window passed, treated as closed
        return CALL_KEYS.some(k => {
          const call = e.calls?.find(c => c.callKey === k);
          return call && call.status === 'Missed';
        });
      }
      // Active Dec'25+ (< 180d): Missed OR unlogged AND 1+ days overdue
      return CALL_KEYS.some(k => {
        if (CALL_DAYS[k] > agingDays) return false;
        const call = e.calls?.find(c => c.callKey === k);
        if (call) return call.status === 'Missed';
        return dayjs().diff(dayjs(doj).add(CALL_DAYS[k], 'day'), 'day') >= 1;
      });
    }).length;

    // ── Summary metrics ──────────────────────────────────────
    const avgProgress = active.length
      ? Math.round(active.reduce((s,e)=>s+getJourneyProgress(e),0)/active.length) : 0;

    const totalDue = active.reduce((s,e)=>{
      if (isLegacy(e)) return s;
      return s+CALL_KEYS.filter(k=>CALL_DAYS[k]<=getAgingDays(e)).length;
    },0);
    // Only count calls that belong to a stage that is already due (avoids inflating connectivity
    // when calls are logged early for future stages)
    const totalLogged = active.reduce((s,e)=>{
      if (isLegacy(e)) return s;
      const agingDays = getAgingDays(e);
      const dueKeys = new Set(CALL_KEYS.filter(k => CALL_DAYS[k] <= agingDays));
      return s + (e.calls?.filter(c => c.status !== 'NA' && dueKeys.has(c.callKey)).length || 0);
    },0);
    const connectivity = totalDue>0?Math.round((totalLogged/totalDue)*100):0;

    // ── Journey health ───────────────────────────────────────
    const healthMap={};
    active.forEach(e=>{ const h=getJourneyHealth(e); healthMap[h]=(healthMap[h]||0)+1; });
    const healthData = HEALTH_ORDER
      .filter(k=>healthMap[k]>0)
      .map(k=>({
        name:k, value:healthMap[k], color:HEALTH_CFG[k].color,
        pct:Math.round(((healthMap[k]||0)/active.length)*100),
      }));

    // ── Call stage funnel — non-legacy only so 'due' is consistent with allCounts ──
    const callFunnel = CALL_KEYS.map(key=>{
      const s=stats?.callSummary?.[key]||{};
      const due=active.filter(e=>!isLegacy(e)&&CALL_DAYS[key]<=getAgingDays(e)).length;
      const comp=s.completed||0, pend=s.pending||0, nor=s.noResponse||0;
      return {
        key, short:CALL_SHORT[key], label:CALL_LABELS[key],
        due, completed:comp, pending:pend, noResponse:nor,
        notDue: Math.max(0,active.length-due),
        rate:   due>0?Math.round((comp/due)*100):0,
      };
    });

    // ── Division breakdown ───────────────────────────────────
    const divMap={};
    active.forEach(e=>{
      const d=e.division||'Unknown';
      if(!divMap[d]) divMap[d]={name:d,total:0,completed:0,onTrack:0,atRisk:0,notStarted:0,noResp:0,progress:0};
      divMap[d].total++;
      const h=getJourneyHealth(e);
      if(h==='Completed')                        divMap[d].completed++;
      else if(h==='On Track'||h==='In Progress') divMap[d].onTrack++;
      else if(h==='At Risk')                     divMap[d].atRisk++;
      else                                        divMap[d].notStarted++;
      if(e.calls?.some(c=>c.status==='No Response')) divMap[d].noResp++;
      divMap[d].progress+=getJourneyProgress(e);
    });
    const divisionData=Object.values(divMap).map(d=>({
      ...d,
      avgProg:Math.round(d.progress/d.total),
      compPct:Math.round((d.completed/d.total)*100),
    })).sort((a,b)=>b.total-a.total);

    // ── Monthly trend (last 6 months) ────────────────────────
    const monthMap={};
    active.forEach(e=>(e.calls||[]).forEach(c=>{
      if(!c.callDate) return;
      const m=dayjs(c.callDate).format('MMM YY');
      if(!monthMap[m]) monthMap[m]={month:m,_sort:dayjs(c.callDate).valueOf(),
        completed:0,noResponse:0,total:0};
      if(c.status==='NA') return; // NA not applicable — exclude from trend
      monthMap[m].total++;
      if(c.status==='Completed')   monthMap[m].completed++;
      if(c.status==='No Response') monthMap[m].noResponse++;
    }));
    const monthlyTrend=Object.values(monthMap)
      .sort((a,b)=>a._sort-b._sort).slice(-6)
      .map(m=>({...m,rate:m.total>0?Math.round((m.completed/m.total)*100):0}));

    // ── Aging buckets ────────────────────────────────────────
    const ageBuckets=[
      {label:'0 – 30 Days',   min:0,   max:30,  color:C.green,  desc:'Fresh onboarding'},
      {label:'31 – 60 Days',  min:31,  max:60,  color:C.blue,   desc:'Early journey'},
      {label:'61 – 90 Days',  min:61,  max:90,  color:C.blue,   desc:'Building habits'},
      {label:'91 – 120 Days', min:91,  max:120, color:C.amber,  desc:'Mid journey'},
      {label:'121 – 180 Days',min:121, max:180, color:C.amber,  desc:'Late journey'},
      {label:'180 + Days',    min:181, max:9999,color:C.red,    desc:'Journey end'},
    ];
    ageBuckets.forEach(b=>{
      b.count=active.filter(e=>{const d=getAgingDays(e);return d>=b.min&&d<=b.max;}).length;
    });

    // ── Recent calls (7 days) ────────────────────────────────
    const recentCalls = active
      .flatMap(e=>(e.calls||[])
        .filter(c=>c.callDate&&dayjs().diff(dayjs(c.callDate),'day')<=7)
        .map(c=>({emp:e,call:c})))
      .sort((a,b)=>dayjs(b.call.callDate).valueOf()-dayjs(a.call.callDate).valueOf())
      .slice(0,12);

    // ── Cohort status counts ────────────────────────────────────
    const defermentCount = employees.filter(e=>
      (e.cohortStatus||'').toLowerCase() === 'deferment'
    ).length;
    const backoutCount   = employees.filter(e=>
      (e.cohortStatus||'').toLowerCase() === 'backout'
    ).length;

    return {
      total:employees.length, activeCount:active.length, resignedCount:resigned.length,
      defermentCount, backoutCount,
      allCounts, pendingEmpsCount, avgProgress, connectivity,
      healthData, callFunnel, divisionData,
      monthlyTrend, ageBuckets, recentCalls,
      atRiskCount:stats?.atRiskCount??atRisk.length,
    };
  },[employees,stats,atRisk]);

  // ── Dec'25+ vs Pre-Dec'25 cohort split ────────────────────────
  const DEC_2025_MS = dayjs('2025-12-01').valueOf();
  const decPlusStats = useMemo(() => {
    if (!employees.length) return null;
    const decPlus = employees.filter(e => {
      const doj = e.dateOfJoining || e.cohortDate;
      return doj && dayjs(doj).valueOf() >= DEC_2025_MS;
    });
    const preDec = employees.filter(e => {
      const doj = e.dateOfJoining || e.cohortDate;
      return doj && dayjs(doj).valueOf() < DEC_2025_MS;
    });
    // Exclusion rules: CCD/Venus (join>Dec'25 + resign>=May'26), After-180 days, Y-code
    const CLOSED_DIVS = new Set(['ccd', 'venus']);
    const MAY_2026_MS = dayjs('2026-05-01').valueOf();
    const isExcluded = e => {
      const doj = e.dateOfJoining || e.cohortDate;
      const div = (e.division || '').trim().toLowerCase();
      if (CLOSED_DIVS.has(div) && doj && dayjs(doj).valueOf() > DEC_2025_MS &&
          e.resignedDate && dayjs(e.resignedDate).valueOf() >= MAY_2026_MS) return true;
      if (doj && e.resignedDate && dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) return true;
      if ((e.empCode || '').toUpperCase().startsWith('Y')) return true;
      return false;
    };
    const decPlusResigned = decPlus.filter(e => e.isResigned && !isExcluded(e)).length;
    // Employees who completed the 180-day journey:
    //   • Active employees with tenure ≥ 180 days (still with us)
    //   • Resigned employees whose tenure AT resignation was ≥ 180 days (completed before leaving)
    const completed180List = decPlus.filter(e => {
      const doj = e.dateOfJoining || e.cohortDate;
      if (!doj) return false;
      if (!e.isResigned) return dayjs().diff(dayjs(doj), 'day') >= 180;
      return e.resignedDate && dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180;
    });
    // inJourney + completed180 + resigned = joined (mutually exclusive, exhaustive)
    const inJourney = decPlus.length - decPlusResigned - completed180List.length;
    return {
      joined:          decPlus.length,
      resigned:        decPlusResigned,
      active:          decPlus.length - decPlusResigned,  // all non-resigned (for retention %)
      inJourney,
      completed180:    completed180List.length,
      completed180List,
      preJoined:       preDec.length,
      preResigned:     preDec.filter(e => e.isResigned).length,
      preActive:       preDec.filter(e => !e.isResigned).length,
    };
  }, [employees, DEC_2025_MS]);

  // ── EC Progress Comparison (6-month quit rate: pre-Connect vs Connect cohorts) ──
  const ecProgressStats = useMemo(() => {
    if (!employees.length) return null;

    const todayStr = dayjs().format('YYYY-MM-DD');
    const todayLbl = dayjs().format('MMM') + " '" + dayjs().format('YY');
    const BRACKETS = [
      { label: `Dec '25–${todayLbl}`, start: '2025-12-01', end: todayStr },
      { label: "Aug '26–Mar '27",     start: '2026-08-01', end: '2027-03-31' },
      { label: "Apr '27–Nov '27",     start: '2027-04-01', end: '2027-11-30' },
    ];

    return BRACKETS.map(b => {
      const bStart = dayjs(b.start);
      const bEnd   = dayjs(b.end);

      // Only employees with an explicit dateOfJoining within this bracket
      const bracketEmps = employees.filter(e => {
        const doj = dayjs(e.dateOfJoining);
        return doj.isValid() && !doj.isBefore(bStart, 'day') && !doj.isAfter(bEnd, 'day');
      });

      // A month qualifies when its 1st day + 180 days ≤ today
      // Dec 1 + 180 = May 30 ✓ | Jan 1 + 180 = Jun 30 ✓ | Feb 1 + 180 = Jul 31 ✓ | Mar 1 + 180 = Aug 29 ✗
      const qualMonths = new Set();
      let m = bStart.startOf('month');
      while (!m.isAfter(bEnd, 'month')) {
        if (!m.startOf('month').add(180, 'day').isAfter(dayjs(), 'day')) {
          qualMonths.add(m.format('YYYY-MM'));
        }
        m = m.add(1, 'month');
      }
      const hiringMonths = qualMonths.size;

      // Fair employees = only those whose dateOfJoining falls in a qualifying month
      const fairEmps = bracketEmps.filter(e => {
        const doj = dayjs(e.dateOfJoining);
        return doj.isValid() && qualMonths.has(doj.format('YYYY-MM'));
      });

      // Quit = resigned before 180 days, excluding CCD/Venus and Y Code
      // Mirrors EC Status matrix exclusion logic exactly
      const CLOSED_DIVS = new Set(['ccd', 'venus']);
      const MAY_2026_MS = dayjs('2026-05-01').valueOf();
      const quit = fairEmps.filter(e => {
        if (!e.isResigned || !e.resignedDate || !e.dateOfJoining) return false;
        // Exclude resigned after completing 180 days (same as EC Status line 761/795)
        if (dayjs(e.resignedDate).diff(dayjs(e.dateOfJoining), 'day') >= 180) return false;
        // Exclude CCD/Venus
        const div = (e.division || '').trim().toLowerCase();
        if (CLOSED_DIVS.has(div) && dayjs(e.resignedDate).valueOf() >= MAY_2026_MS) return false;
        // Exclude Y Code
        if ((e.empCode || '').toUpperCase().startsWith('Y')) return false;
        return true;
      }).length;

      const qpm = hiringMonths > 0 ? Math.round((quit / hiringMonths) * 10) / 10 : null;

      return { ...b, quit, hiringMonths, qpm, fairTotal: fairEmps.length, hasData: fairEmps.length > 0 };
    }).filter(b => b.hasData || b.start === '2025-12-01');
  }, [employees]);

  // ── Compliance analytics ────────────────────────────────────────
  const compStats = useMemo(() => {
    if (!compliance.length) return null;
    const total   = compliance.length;
    const yes     = v => (v||'').trim().toLowerCase() === 'yes';
    const tgpDone = compliance.filter(r => yes(r.tgpCompliance)).length;
    const ackDone = compliance.filter(r => yes(r.acknowledgementCompliance)).length;
    const indDone = compliance.filter(r => yes(r.inductionCompliance)).length;
    const allDone = compliance.filter(r => yes(r.tgpCompliance) && yes(r.acknowledgementCompliance) && yes(r.inductionCompliance)).length;
    const noneDone= compliance.filter(r => !yes(r.tgpCompliance) && !yes(r.acknowledgementCompliance) && !yes(r.inductionCompliance)).length;
    const desgMap = {};
    compliance.forEach(r => {
      const d = r.designation||'Unknown';
      if (!desgMap[d]) desgMap[d] = { designation:d, tgp:0, ack:0, ind:0, total:0 };
      desgMap[d].total++;
      if (yes(r.tgpCompliance))             desgMap[d].tgp++;
      if (yes(r.acknowledgementCompliance)) desgMap[d].ack++;
      if (yes(r.inductionCompliance))       desgMap[d].ind++;
    });
    const byDesignation = Object.values(desgMap).sort((a,b)=>b.total-a.total).slice(0,10);
    return { total, tgpDone, ackDone, indDone, allDone, noneDone, byDesignation };
  }, [compliance]);

  // ── At-risk table columns ────────────────────────────────────
  const riskCols = [
    { title:'Employee', width:210,
      render:(_,r)=>(
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <Avatar size={34} style={{background:avatarColor(r.empName),
            fontSize:11,fontWeight:700,flexShrink:0}}>
            {r.empName?.split(' ').map(w=>w[0]).slice(0,2).join('')}
          </Avatar>
          <div>
            <div style={{fontWeight:700,fontSize:13,color:C.text1}}>{r.empName}</div>
            <div style={{fontSize:11,color:C.text3}}>{r.empCode} · {r.division}</div>
          </div>
        </div>
      )},
    { title:'HQ / Location', dataIndex:'hq', width:120,
      render:v=><span style={{fontSize:12,color:C.text2}}>{v||'—'}</span> },
    { title:'Days in Journey', width:110, align:'center',
      render:(_,r)=>{
        const d=getAgingDays(r);
        return <Tag color={d>120?'error':d>60?'warning':'blue'}
          style={{borderRadius:20,fontWeight:700}}>Day {d}</Tag>;
      }},
    { title:'No Response Stage', width:140, align:'center',
      render:(_,r)=>{
        const c=r.calls?.find(c=>c.status==='No Response');
        return c?<Tag color="error" style={{borderRadius:20,fontWeight:700}}>
          {c.callKey?.replace('day','Day ')}
        </Tag>:'—';
      }},
    { title:'Journey', width:150,
      render:(_,r)=>{
        const pct=getJourneyProgress(r);
        return <Bar_ pct={pct} color={pct>50?C.blue:C.amber}/>;
      }},
    { title:'', width:80, fixed:'right',
      render:(_,r)=>(
        <Button size="small" type="primary" icon={<EyeOutlined/>}
          onClick={()=>nav(`/employees/${r.id}`)}
          style={{background:C.navy,borderColor:C.navy,borderRadius:6,fontWeight:600}}>
          View
        </Button>
      )},
  ];

  // ── Render ────────────────────────────────────────────────────
  return (
    <div style={{background:'#f0f4f8',minHeight:'100vh'}}>

      {/* ══ HEADER ═════════════════════════════════════════════ */}
      <div style={{
        background:`linear-gradient(135deg,${C.navy} 0%,#2a4a9f 100%)`,
        padding:'22px 28px',marginBottom:24,
        boxShadow:'0 4px 20px rgba(27,45,107,0.3)',
      }}>
        <div style={{display:'flex',justifyContent:'space-between',
          alignItems:'center',flexWrap:'wrap',gap:12}}>
          <div>
            <div style={{fontSize:22,fontWeight:800,color:'#fff',
              letterSpacing:'-0.5px',display:'flex',alignItems:'center',gap:10}}>
              📊 ErisConnect Dashboard
            </div>
            <div style={{fontSize:12,color:'rgba(255,255,255,0.55)',marginTop:3}}>
              {dayjs().format('dddd, DD MMMM YYYY')}
              &nbsp;·&nbsp;180-Day Employee Journey Tracker &nbsp;·&nbsp; Data from October 2025 onwards
            </div>
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <Button
              icon={<SyncOutlined spin={syncLoading}/>}
              onClick={handleSharepointSync}
              loading={syncLoading}
              style={{borderRadius:8,background:'rgba(255,255,255,0.18)',
                border:'1px solid rgba(255,255,255,0.4)',color:'#fff',fontWeight:700}}>
              Sync SharePoint
            </Button>
            <Button icon={<ReloadOutlined/>} onClick={loadAll} loading={loading}
              style={{borderRadius:8,background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)',color:'#fff',fontWeight:600}}>
              Refresh
            </Button>
            <Button icon={<PlusOutlined/>} onClick={()=>nav('/add')}
              style={{borderRadius:8,background:'#fff',border:'none',
                color:C.navy,fontWeight:700}}>
              Add Employee
            </Button>
            <Button icon={<UploadOutlined/>} onClick={()=>nav('/bulk-upload')}
              style={{borderRadius:8,background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)',color:'#fff'}}>
              Bulk Upload
            </Button>
          </div>
        </div>
      </div>

      <div style={{padding:'0 24px 48px'}}>

        {error&&<Alert message={error} type="error" showIcon closable
          style={{marginBottom:20,borderRadius:10}} onClose={()=>setError('')}/>}

        {/* ══ ROW 1 — 3 KPI CARDS (Dec'25+ cohort) ═══════════════ */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button
            onClick={() => setShowHistorical(v => !v)}
            title="View Oct '25 · Nov '25 historical cohort data"
            style={{
              padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
              border: `1.5px solid ${showHistorical ? '#7c3aed' : '#c4b5fd'}`,
              background: showHistorical ? '#7c3aed' : '#f5f3ff',
              color: showHistorical ? '#fff' : '#7c3aed',
            }}
          >
            📋 {showHistorical ? 'Hide' : 'Show'} Oct '25 · Nov '25
          </button>
        </div>

        {showHistorical && decPlusStats && (
          <div style={{ marginBottom: 16, borderRadius: 12, overflow: 'hidden',
            border: '1.5px dashed #7c3aed', background: '#faf5ff' }}>
            <div style={{ background: '#7c3aed', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 16 }}>📋</span>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Oct '25 · Nov '25 — Historical Cohort</span>
              <span style={{ color: '#ddd6fe', fontSize: 11 }}>180-day journey completed · shown for reference only</span>
            </div>
            <div style={{ padding: '10px 14px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { icon: '👥', label: 'Joined',    val: decPlusStats.preJoined,   color: C.navy,  bg: '#eff6ff' },
                { icon: '🚪', label: 'Resigned',  val: decPlusStats.preResigned, color: C.red,   bg: '#fef2f2' },
                { icon: '✅', label: 'Active',    val: decPlusStats.preActive,   color: C.green, bg: '#f0fdf4' },
                { icon: '📊', label: 'Attrition %', val: decPlusStats.preJoined > 0 ? Math.round((decPlusStats.preResigned / decPlusStats.preJoined) * 100) + '%' : '0%', color: C.amber, bg: '#fffbeb' },
              ].map(k => (
                <div key={k.label} style={{ padding: '8px 14px', borderRadius: 8, flex: 1, minWidth: 90,
                  background: k.bg, border: `1px solid ${k.color}33` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ fontSize: 16 }}>{k.icon}</span>
                    <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.val}</div>
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginTop: 3 }}>{k.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',
          gap:16,marginBottom:24}}>
          {[
            { icon:'👥', label:'Total Employees',
              value: decPlusStats?.joined ?? attrStats?.total ?? a?.total,
              sub:'Dec \'25 onwards cohort',
              color:C.navy, bg:'#eff6ff',
              note:`${decPlusStats?.inJourney??0} active · ${decPlusStats?.completed180??0} completed · ${decPlusStats?.resigned??0} resigned` },
            { icon:'✅', label:'Total Active',
              value: decPlusStats?.inJourney ?? attrStats?.totalActive ?? a?.activeCount,
              sub: decPlusStats ? `${decPlusStats.joined > 0 ? Math.round(((decPlusStats.inJourney??0) / decPlusStats.joined) * 100) : 0}% of total enrolled` : '',
              color:C.green, bg:C.lgreen,
              note:'Currently on 180-day journey' },
            { icon:'🚪', label:'Total Resigned',
              value: decPlusStats?.resigned ?? attrStats?.totalResigned ?? a?.resignedCount,
              sub: decPlusStats ? `${decPlusStats.joined > 0 ? Math.round((decPlusStats.resigned / decPlusStats.joined) * 100) : 0}% attrition rate` : '',
              color:C.red, bg:C.lred,
              note:'Left the organisation' },
            { icon:'🎓', label:'Completed 180-Day Journey',
              value: decPlusStats?.completed180 ?? 0,
              sub: decPlusStats?.joined > 0
                ? `${Math.round(((decPlusStats.completed180 ?? 0) / decPlusStats.joined) * 100)}% of total enrolled · 180+ days tenure`
                : 'Dec \'25 onwards · 180+ days tenure',
              color:'#7c3aed', bg:'#f5f3ff',
              note:'Click to view employee list',
              onClick: () => setShow180Modal(true) },
          ].map((k,i)=>(
            <div key={i} style={{
              background:k.bg, borderRadius:16,
              border:`1px solid ${k.color}22`,
              borderTop:`4px solid ${k.color}`,
              padding:'22px 24px',
              boxShadow:'0 1px 4px rgba(0,0,0,0.05)',
              transition:'transform .18s, box-shadow .18s',
              cursor: k.onClick ? 'pointer' : 'default',
              display:'flex', alignItems:'center', gap:20,
            }}
            onClick={k.onClick}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 8px 24px ${k.color}22`;}}
            onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.05)';}}>
              <div style={{fontSize:40,lineHeight:1}}>{k.icon}</div>
              <div style={{flex:1}}>
                {loading
                  ? <div style={{height:36,background:`${k.color}22`,borderRadius:6,marginBottom:8,width:100}}/>
                  : <div style={{fontSize:38,fontWeight:900,color:k.color,
                      lineHeight:1,marginBottom:4,letterSpacing:'-2px'}}>
                      {k.value??'—'}
                    </div>
                }
                <div style={{fontSize:13,fontWeight:800,color:'#4a6580',
                  textTransform:'uppercase',letterSpacing:'0.7px',marginBottom:3}}>
                  {k.label}
                </div>
                <div style={{fontSize:12,color:k.color,fontWeight:600,marginBottom:2}}>{k.sub}</div>
                <div style={{fontSize:11,color: k.onClick ? k.color : '#7a96b0', fontWeight: k.onClick ? 600 : 400}}>
                  {k.onClick ? '👆 ' : ''}{k.note}
                </div>
              </div>
            </div>
          ))}
        </div>
 {/* ══ EC STATUS — JOIN MONTH × RESIGN MONTH ══════════════ */}
        {(() => {
          const resignedList = attrStats?.resignedList || [];
          const cohortTenure = attrStats?.cohortTenure || [];
          if (!resignedList.length || !cohortTenure.length) return null;

          // Exclude CCD/Venus (join>Dec'25 + resign>=May'26), After-180 days, Y-code
          const CLOSED_DIVS_SET   = new Set(['ccd', 'venus']);
          const MAY_2026_MATRIX   = dayjs('2026-05-01').valueOf();
          const DEC_2025_MATRIX   = dayjs('2025-12-01').valueOf();
          const resignedListForMatrix = resignedList.filter(e => {
            const div = (e.division || '').trim().toLowerCase();
            const doj = e.dateOfJoining || e.cohortDate;
            if (CLOSED_DIVS_SET.has(div) && doj && dayjs(doj).valueOf() > DEC_2025_MATRIX &&
                e.resignedDate && dayjs(e.resignedDate).valueOf() >= MAY_2026_MATRIX) return false;
            if (doj && e.resignedDate && dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) return false;
            if ((e.empCode || '').toUpperCase().startsWith('Y')) return false;
            return true;
          });

          // All unique resign months sorted chronologically
          const monthSet = new Set();
          resignedListForMatrix.forEach(e => {
            if (e.resignedDate) monthSet.add(dayjs(e.resignedDate).format('MMM YYYY'));
          });
          const resignMonths = [...monthSet].sort((a, b) =>
            dayjs(a, 'MMM YYYY').valueOf() - dayjs(b, 'MMM YYYY').valueOf()
          );
          if (!resignMonths.length) return null;

          // Cohort badge count from cohortTenure (API) — used only for "N cohorts" label
          const cohortCountPerMonth = {};
          cohortTenure.filter(ct => ct.totalEmployees > 0).forEach(ct => {
            const parts   = ct.cohort?.split(' · ') || [];
            const rawDate = parts[1] || '';
            const jm = rawDate ? dayjs(rawDate, 'DD MMM YY').format('MMM YYYY') : null;
            if (!jm) return;
            cohortCountPerMonth[jm] = (cohortCountPerMonth[jm] || 0) + 1;
          });

          // Joined count from employees array — same source as Net/CCD/Y columns so tally holds
          const joinedPerMonth = {};
          employees.forEach(e => {
            const doj = e.dateOfJoining || e.cohortDate;
            if (!doj) return;
            if (dayjs(doj).valueOf() < DEC_2025_MATRIX) return;
            const jm = dayjs(doj).format('MMM YYYY');
            joinedPerMonth[jm] = (joinedPerMonth[jm] || 0) + 1;
          });

          // Per resigned employee: bucket by dateOfJoining month × resignedDate month
          const perMonthMap = {};
          resignedListForMatrix.forEach(e => {
            if (!e.resignedDate) return;
            const doj = e.dateOfJoining || e.cohortDate;
            if (!doj) return;
            // Employees who completed their 180-day journey — not counted as EC attrition
            if (dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) return;
            const jm = dayjs(doj).format('MMM YYYY');
            const rm = dayjs(e.resignedDate).format('MMM YYYY');
            if (!perMonthMap[jm]) {
              perMonthMap[jm] = {};
              resignMonths.forEach(m => { perMonthMap[jm][m] = 0; });
            }
            if (perMonthMap[jm][rm] !== undefined) perMonthMap[jm][rm]++;
          });

          // Count excluded employees per join-month for informational columns
          const after180ByJoinMonth = {};
          const ccdVenusByJoinMonth = {};
          const yCodeByJoinMonth    = {};
          resignedList.forEach(e => {
            const doj = e.dateOfJoining || e.cohortDate;
            if (!doj || !e.resignedDate) return;
            const jm = dayjs(doj).format('MMM YYYY');
            if (dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) {
              after180ByJoinMonth[jm] = (after180ByJoinMonth[jm] || 0) + 1;
            }
            const div = (e.division || '').trim().toLowerCase();
            if (CLOSED_DIVS_SET.has(div) && dayjs(doj).valueOf() > DEC_2025_MATRIX &&
                e.resignedDate && dayjs(e.resignedDate).valueOf() >= MAY_2026_MATRIX) {
              ccdVenusByJoinMonth[jm] = (ccdVenusByJoinMonth[jm] || 0) + 1;
            }
            if ((e.empCode || '').toUpperCase().startsWith('Y')) {
              yCodeByJoinMonth[jm] = (yCodeByJoinMonth[jm] || 0) + 1;
            }
          });

          // Breakdown columns computed from employees array (same source, ensures tally)
          const netJoinedPerMonth         = {};  // excl. CCD/Venus + Y-code (all statuses)
          const totalCCDVenusByJoinMonth  = {};  // ALL CCD/Venus: active + resigned
          const totalYCodeByJoinMonth     = {};  // ALL Y-code:    active + resigned
          const activeCCDVenusByJoinMonth = {};  // CCD/Venus active only (for hidden col)
          const activeYCodeByJoinMonth    = {};  // Y-code active only    (for hidden col)
          employees.forEach(e => {
            const doj = e.dateOfJoining || e.cohortDate;
            if (!doj) return;
            if (dayjs(doj).valueOf() < DEC_2025_MATRIX) return;
            const div        = (e.division || '').trim().toLowerCase();
            const isYCode    = (e.empCode || '').toUpperCase().startsWith('Y');
            const isCcdVenus = CLOSED_DIVS_SET.has(div);
            const isActive   = !e.isResigned;
            const jm         = dayjs(doj).format('MMM YYYY');
            if (!isCcdVenus && !isYCode) {
              netJoinedPerMonth[jm]        = (netJoinedPerMonth[jm]        || 0) + 1;
            }
            if (isCcdVenus) {
              totalCCDVenusByJoinMonth[jm] = (totalCCDVenusByJoinMonth[jm] || 0) + 1;
              if (isActive) activeCCDVenusByJoinMonth[jm] = (activeCCDVenusByJoinMonth[jm] || 0) + 1;
            }
            if (isYCode) {
              totalYCodeByJoinMonth[jm]    = (totalYCodeByJoinMonth[jm]    || 0) + 1;
              if (isActive) activeYCodeByJoinMonth[jm] = (activeYCodeByJoinMonth[jm] || 0) + 1;
            }
          });

          // Merge into rows grouped by join month
          const allJoinMonths = new Set([
            ...Object.keys(joinedPerMonth),
            ...Object.keys(perMonthMap),
          ]);
          const monthRows = [...allJoinMonths]
            .sort((a, b) => dayjs(a, 'MMM YYYY').valueOf() - dayjs(b, 'MMM YYYY').valueOf())
            .map(jm => {
              const totalJoined     = joinedPerMonth[jm] || 0;
              const netJoined       = netJoinedPerMonth[jm] || 0;
              const cohortCount     = cohortCountPerMonth[jm] || 0;
              const perResign       = perMonthMap[jm] || Object.fromEntries(resignMonths.map(m => [m, 0]));
              const totalResigned   = Object.values(perResign).reduce((s, v) => s + v, 0);
              const after180Resigned  = after180ByJoinMonth[jm]       || 0;
              const ccdVenusResigned  = ccdVenusByJoinMonth[jm]       || 0;
              const yCodeResigned     = yCodeByJoinMonth[jm]          || 0;
              const totalCCDVenus     = totalCCDVenusByJoinMonth[jm]  || 0;
              const totalYCode        = totalYCodeByJoinMonth[jm]     || 0;
              const activeCCDVenus    = activeCCDVenusByJoinMonth[jm] || 0;
              const activeYCode       = activeYCodeByJoinMonth[jm]    || 0;
              const active          = totalJoined - totalResigned - after180Resigned - ccdVenusResigned - yCodeResigned;
              const lossPct         = totalJoined > 0 ? Math.round((totalResigned / totalJoined) * 100) : 0;
              const netLossPct      = netJoined   > 0 ? Math.round((totalResigned / netJoined)   * 100) : 0;
              const retainPct       = totalJoined > 0 ? Math.round((active / totalJoined) * 100) : 0;
              const label           = dayjs(jm, 'MMM YYYY').format("MMM'YY");
              return { jm, label, totalJoined, netJoined, totalCCDVenus, totalYCode, totalResigned, active, lossPct, netLossPct, retainPct, perResign, cohortCount, after180Resigned, ccdVenusResigned, yCodeResigned, activeCCDVenus, activeYCode };
            });

          if (!monthRows.length) return null;

          // Only show Dec'25 onwards — earlier cohorts have completed their 180-day journey
          const DEC_2025 = dayjs('2025-12-01').valueOf();
          const visibleRows = monthRows.filter(
            r => dayjs(r.jm, 'MMM YYYY').valueOf() >= DEC_2025
          );
          const preDec25Rows = monthRows.filter(
            r => dayjs(r.jm, 'MMM YYYY').valueOf() < DEC_2025
          );
          // Historical (Oct'25 + Nov'25): use full resignedList for actual attrition counts
          const histResignedPerMonth = {};
          resignedList.forEach(e => {
            const doj = e.dateOfJoining || e.cohortDate;
            if (!doj || dayjs(doj).valueOf() >= DEC_2025) return;
            const jm = dayjs(doj).format('MMM YYYY');
            histResignedPerMonth[jm] = (histResignedPerMonth[jm] || 0) + 1;
          });
          const histJoined   = preDec25Rows.reduce((s, r) => s + r.totalJoined, 0);
          const histResigned = Object.values(histResignedPerMonth).reduce((s, v) => s + v, 0);
          const histActive   = histJoined - histResigned;
          const histAttrPct  = histJoined > 0 ? Math.round((histResigned / histJoined) * 100) : 0;
          // Resign-month columns: Dec'25+ joiners cannot resign before Dec'25
          const visibleResignMonths = resignMonths.filter(
            m => dayjs(m, 'MMM YYYY').valueOf() >= DEC_2025
          );

          // Totals (full — all rows, used for overall KPI)
          const totJoined   = monthRows.reduce((s, r) => s + r.totalJoined,   0);
          const totResigned = monthRows.reduce((s, r) => s + r.totalResigned, 0);
          const totActive   = totJoined - totResigned;

          // EC-specific totals — Dec'25+ visible rows only
          const ecTotal       = visibleRows.reduce((s, r) => s + r.totalJoined,   0);
          const ecNetJoined   = visibleRows.reduce((s, r) => s + r.netJoined,     0);
          const ecResigned = visibleRows.reduce((s, r) => s + r.totalResigned,    0);
          const ecActive   = ecTotal - ecResigned;
          const ecAttrPct     = ecTotal     > 0 ? Math.round((ecResigned / ecTotal)     * 100) : 0;
          const ecNetAttrPct  = ecNetJoined > 0 ? Math.round((ecResigned / ecNetJoined) * 100) : 0;
          const ecAfter180       = visibleRows.reduce((s, r) => s + r.after180Resigned, 0);
          const ecCcdVenus       = visibleRows.reduce((s, r) => s + r.ccdVenusResigned, 0);
          const ecYCode          = visibleRows.reduce((s, r) => s + r.yCodeResigned,    0);
          const ecTotalCCDVenus  = visibleRows.reduce((s, r) => s + r.totalCCDVenus,   0);
          const ecTotalYCode     = visibleRows.reduce((s, r) => s + r.totalYCode,     0);
          const ecActiveCCDVenus = visibleRows.reduce((s, r) => s + r.activeCCDVenus, 0);
          const ecActiveYCode    = visibleRows.reduce((s, r) => s + r.activeYCode,    0);
          const ecTrueActive  = ecTotal - ecResigned - ecAfter180 - ecCcdVenus - ecYCode;

          const totPerMonth = {};
          visibleResignMonths.forEach(m => {
            totPerMonth[m] = visibleRows.reduce((s, r) => s + (r.perResign[m] || 0), 0);
          });

          // Under-10-days per resign month (Dec'25+ joiners only)
          const under10 = {};
          visibleResignMonths.forEach(m => { under10[m] = 0; });
          resignedListForMatrix.forEach(e => {
            if (!e.resignedDate || !e.dateOfJoining) return;
            if (dayjs(e.dateOfJoining).valueOf() < DEC_2025) return;
            const days = dayjs(e.resignedDate).diff(dayjs(e.dateOfJoining), 'day');
            if (days <= 10) {
              const m = dayjs(e.resignedDate).format('MMM YYYY');
              if (under10[m] !== undefined) under10[m]++;
            }
          });
          const totUnder10    = Object.values(under10).reduce((s, v) => s + v, 0);
          const visibleResigned = visibleRows.reduce((s, r) => s + r.totalResigned, 0);
          const under10Pct    = visibleResigned > 0 ? Math.round((totUnder10 / visibleResigned) * 100) : 0;

          // Drill-down helpers — each returns an employee list for the clicked cell
          const openDrill = (title, emps) => setDrillDown({ title, emps });

          const getResignCellEmps = (joinMonth, resignMonth) =>
            resignedListForMatrix.filter(e => {
              const doj = e.dateOfJoining || e.cohortDate;
              if (!doj || !e.resignedDate) return false;
              return dayjs(doj).format('MMM YYYY') === joinMonth &&
                     dayjs(e.resignedDate).format('MMM YYYY') === resignMonth;
            });

          const getJoinedEmps = (joinMonth) =>
            employees.filter(e => {
              const doj = e.dateOfJoining || e.cohortDate;
              if (!doj) return false;
              if (dayjs(doj).valueOf() < DEC_2025_MATRIX) return false;
              return dayjs(doj).format('MMM YYYY') === joinMonth;
            });

          const getNetEmps = (joinMonth) =>
            employees.filter(e => {
              const doj = e.dateOfJoining || e.cohortDate;
              if (!doj) return false;
              if (dayjs(doj).valueOf() < DEC_2025_MATRIX) return false;
              if ((e.empCode || '').toUpperCase().startsWith('Y')) return false;
              const div = (e.division || '').trim().toLowerCase();
              if (CLOSED_DIVS_SET.has(div)) return false;
              return dayjs(doj).format('MMM YYYY') === joinMonth;
            });

          const getCCDVenusEmps = (joinMonth) =>
            employees.filter(e => {
              const doj = e.dateOfJoining || e.cohortDate;
              if (!doj) return false;
              if (dayjs(doj).valueOf() < DEC_2025_MATRIX) return false;
              const div = (e.division || '').trim().toLowerCase();
              return CLOSED_DIVS_SET.has(div) && dayjs(doj).format('MMM YYYY') === joinMonth;
            });

          const getYCodeEmps = (joinMonth) =>
            employees.filter(e => {
              const doj = e.dateOfJoining || e.cohortDate;
              if (!doj) return false;
              if (dayjs(doj).valueOf() < DEC_2025_MATRIX) return false;
              return (e.empCode || '').toUpperCase().startsWith('Y') &&
                     dayjs(doj).format('MMM YYYY') === joinMonth;
            });

          const getTotalResignedEmps = (joinMonth) =>
            resignedListForMatrix.filter(e => {
              const doj = e.dateOfJoining || e.cohortDate;
              if (!doj) return false;
              return dayjs(doj).format('MMM YYYY') === joinMonth;
            });

          const getActiveEmps = (joinMonth) =>
            employees.filter(e => {
              const doj = e.dateOfJoining || e.cohortDate;
              if (!doj) return false;
              if (dayjs(doj).valueOf() < DEC_2025_MATRIX) return false;
              if ((e.empCode || '').toUpperCase().startsWith('Y')) return false;
              const div = (e.division || '').trim().toLowerCase();
              if (CLOSED_DIVS_SET.has(div)) return false;
              if (e.isResigned) return false;
              return dayjs(doj).format('MMM YYYY') === joinMonth;
            });

          const getUnder10Emps = (resignMonth) =>
            resignedListForMatrix.filter(e => {
              if (!e.resignedDate || !e.dateOfJoining) return false;
              if (dayjs(e.dateOfJoining).valueOf() < DEC_2025_MATRIX) return false;
              const days = dayjs(e.resignedDate).diff(dayjs(e.dateOfJoining), 'day');
              return days <= 10 && dayjs(e.resignedDate).format('MMM YYYY') === resignMonth;
            });

          // Styles
          const TH = (extra = {}) => ({
            padding: '9px 10px', textAlign: 'center', fontSize: 11,
            fontWeight: 700, color: '#fff',
            borderRight: '1px solid rgba(255,255,255,0.15)',
            whiteSpace: 'nowrap', background: C.navy, ...extra,
          });
          const TD = (bg = 'transparent') => ({
            padding: '8px 10px', textAlign: 'center', fontSize: 12,
            fontWeight: 600, borderRight: `1px solid ${C.border}`,
            background: bg, whiteSpace: 'nowrap',
          });

          return (
            <div style={{ marginBottom: 24 }}>

              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-end', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text1,
                    display: 'flex', alignItems: 'center', gap: 8 }}>
                    📊 EC Status
                    <span style={{ fontSize: 12, fontWeight: 400, color: C.text3 }}>
                      — Join Month × Resign Month
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: C.text3, marginTop: 3 }}>
                    All cohorts in same month merged · columns = which month they resigned
                  </div>
                </div>
                {attrLoading && <span style={{ fontSize: 12, color: C.text3 }}>Loading…</span>}
              </div>

              {/* Oct'25 · Nov'25 historical cohort panel */}
              {showHistorical && preDec25Rows.length > 0 && (
                <div style={{ marginBottom: 14, borderRadius: 12, overflow: 'hidden',
                  border: '1.5px dashed #7c3aed' }}>
                  <div style={{ background: '#7c3aed', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>📋 Oct '25 · Nov '25 — Historical Cohort</span>
                    <span style={{ color: '#ddd6fe', fontSize: 11 }}>180-day journey completed · for reference only</span>
                  </div>
                  <div style={{ background: '#faf5ff', padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                      {[
                        { icon: '👥', label: 'Joined',      val: histJoined,   color: C.blue,  bg: '#eff6ff' },
                        { icon: '🚪', label: 'Resigned',    val: histResigned, color: C.red,   bg: '#fef2f2' },
                        { icon: '✅', label: 'Active',      val: histActive,   color: C.green, bg: '#f0fdf4' },
                        { icon: '📊', label: 'Attrition %', val: histAttrPct + '%', color: C.amber, bg: '#fffbeb' },
                      ].map(k => (
                        <div key={k.label} style={{ padding: '8px 12px', borderRadius: 8, flex: 1, minWidth: 90,
                          background: k.bg, border: `1px solid ${k.color}33` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ fontSize: 16 }}>{k.icon}</span>
                            <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.val}</div>
                          </div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginTop: 3 }}>{k.label}</div>
                        </div>
                      ))}
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#7c3aed' }}>
                          {['Join Month', 'Joined', 'Resigned', 'Active', '% Loss'].map(h => (
                            <th key={h} style={{ padding: '6px 10px', color: '#fff', fontWeight: 700,
                              textAlign: h === 'Join Month' ? 'left' : 'center',
                              borderRight: '1px solid rgba(255,255,255,0.2)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preDec25Rows.map((row, i) => {
                          const res = histResignedPerMonth[row.jm] || 0;
                          const act = row.totalJoined - res;
                          const lp  = row.totalJoined > 0 ? Math.round((res / row.totalJoined) * 100) : 0;
                          return (
                            <tr key={row.jm} style={{ background: i % 2 === 0 ? '#f5f3ff' : '#fff',
                              borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '7px 10px', fontWeight: 700, color: '#7c3aed' }}>{row.label}</td>
                              <td style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 700, color: C.blue }}>{row.totalJoined}</td>
                              <td style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 700, color: C.red }}>{res}</td>
                              <td style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 700, color: C.green }}>{act}</td>
                              <td style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 700,
                                color: lp > 20 ? C.red : lp > 10 ? C.amber : C.green }}>{lp}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* EC summary strip (Dec'25+ only) */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                {[
                  { icon: '👥', val: decPlusStats?.joined ?? ecTotal, label: 'EC Total Joined', color: C.blue, bg: '#eff6ff' },
                  { icon: '🚪', val: ecResigned,        label: 'EC Total Resigned', color: C.red,     bg: '#fef2f2' },
                  { icon: '✅', val: decPlusStats?.inJourney ?? 0, label: 'EC Total Active', color: C.green, bg: '#f0fdf4' },
                  { icon: '📊', val: ecAttrPct + '%',   label: 'EC Attrition %',   color: C.amber,   bg: '#fffbeb' },
                  ...(showInfoCols ? [
                    { icon: '⏳', val: ecAfter180,       label: 'After 180',         color: '#7c3aed', bg: '#f5f3ff' },
                    { icon: '🏢', val: ecCcdVenus,       label: 'CCD / Venus',       color: '#ea580c', bg: '#fff7ed' },
                    { icon: '🏷️', val: ecYCode,          label: 'Y Code',            color: '#059669', bg: '#ecfdf5' },
                  ] : []),
                ].map(k => (
                  <div key={k.label} style={{
                    padding: '10px 14px', borderRadius: 10, flex: 1, minWidth: 95,
                    background: k.bg, border: `1px solid ${k.color}33`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 18, lineHeight: 1 }}>{k.icon}</span>
                      <div style={{ fontSize: 22, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.val}</div>
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.text3,
                      textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 5 }}>{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Table */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: 8 }}>
                <button
                  onClick={() => setShowBreakdownCols(v => !v)}
                  style={{
                    padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', border: `1.5px solid ${showBreakdownCols ? '#0369a1' : C.border}`,
                    background: showBreakdownCols ? '#e0f2fe' : C.white,
                    color: showBreakdownCols ? '#0369a1' : C.text3,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {showBreakdownCols ? '▲ Hide' : '▼ Show'} Net / CCD/Venus / Y Code / % Loss on Net
                </button>
                <button
                  onClick={() => setShowInfoCols(v => !v)}
                  style={{
                    padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', border: `1.5px solid ${showInfoCols ? '#7c3aed' : C.border}`,
                    background: showInfoCols ? '#f5f3ff' : C.white,
                    color: showInfoCols ? '#7c3aed' : C.text3,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {showInfoCols ? '▲ Hide' : '▼ Show'} After 180 / CCD/Venus / Y Code
                </button>
              </div>
              <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`,
                overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                    <thead>
                      {/* Group row */}
                      <tr style={{ background: '#0f2352' }}>
                        <th colSpan={showBreakdownCols ? 5 : 2} style={{ ...TH({ textAlign: 'left', background: '#0f2352',
                          borderRight: '1px solid rgba(255,255,255,0.25)' }) }}>
                          Join Month
                        </th>
                        <th colSpan={visibleResignMonths.length} style={{ ...TH({ background: '#0f2352',
                          borderRight: '1px solid rgba(255,255,255,0.25)', color: '#fca5a5' }) }}>
                          ← Resigned in Month →
                        </th>
                        <th colSpan={(showBreakdownCols ? 5 : 4) + (showInfoCols ? 3 : 0)} style={{ ...TH({ background: '#0f2352',
                          borderRight: 'none', color: '#86efac' }) }}>
                          Summary
                        </th>
                      </tr>
                      {/* Column headers */}
                      <tr>
                        <th style={{ ...TH({ textAlign: 'left', minWidth: 100,
                          position: 'sticky', left: 0 }) }}>Month</th>
                        <th style={{ ...TH({ minWidth: 70, color: '#93c5fd' }) }}>Joined</th>
                        {showBreakdownCols && <>
                          <th style={{ ...TH({ minWidth: 70, color: '#6ee7b7' }) }}>
                            Net<br/><span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>excl. CCD/Venus/Y</span>
                          </th>
                          <th style={{ ...TH({ minWidth: 72, color: '#fdba74' }) }}>
                            CCD/Venus<br/><span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>Total</span>
                          </th>
                          <th style={{ ...TH({ minWidth: 68, color: '#c4b5fd' }) }}>
                            Y Code<br/><span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>Total</span>
                          </th>
                        </>}
                        {visibleResignMonths.map(m => (
                          <th key={m} style={{ ...TH({ minWidth: 62, color: '#fca5a5' }) }}>
                            {dayjs(m, 'MMM YYYY').format('MMM')}<br/>
                            <span style={{ fontSize: 10, opacity: 0.75, fontWeight: 500 }}>
                              {dayjs(m, 'MMM YYYY').format('YY')}
                            </span>
                          </th>
                        ))}
                        <th style={{ ...TH({ minWidth: 72, color: '#fca5a5' }) }}>Resigned</th>
                        <th style={{ ...TH({ minWidth: 68, color: '#86efac' }) }}>Active</th>
                        <th style={{ ...TH({ minWidth: 66, color: '#fde68a' }) }}>% Loss</th>
                        {showBreakdownCols && (
                          <th style={{ ...TH({ minWidth: 76, color: '#fcd34d' }) }}>
                            % Loss<br/><span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>on Net</span>
                          </th>
                        )}
                        <th style={{ ...TH({ minWidth: 66, color: '#86efac', ...(!showInfoCols && { borderRight: 'none' }) }) }}>% Active</th>
                        {showInfoCols && <>
                          <th style={{ ...TH({ minWidth: 90, color: '#c4b5fd' }) }}>
                            After 180<br/><span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>Resigned</span>
                          </th>
                          <th style={{ ...TH({ minWidth: 90, color: '#fdba74' }) }}>
                            CCD/Venus<br/><span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>Resigned</span>
                          </th>
                          <th style={{ ...TH({ minWidth: 80, color: '#6ee7b7', borderRight: 'none' }) }}>
                            Y Code<br/><span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>Resigned</span>
                          </th>
                        </>}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map((row, i) => (
                        <tr key={row.jm} style={{ borderBottom: `1px solid ${C.border}`,
                          background: i % 2 === 0 ? C.white : '#fafcff' }}>
                          {/* Month label */}
                          <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700,
                            color: C.navy, position: 'sticky', left: 0,
                            background: i % 2 === 0 ? C.white : '#fafcff',
                            borderRight: `1px solid ${C.border}` }}>
                            {row.label}
                            {row.cohortCount > 1 && (
                              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600,
                                color: C.blue, background: '#eff6ff', padding: '1px 6px',
                                borderRadius: 10, border: '1px solid #bfdbfe' }}>
                                {row.cohortCount} cohorts
                              </span>
                            )}
                          </td>
                          {/* Joined */}
                          <td style={TD('#eff6ff')}>
                            <span style={{ fontWeight: 800, color: C.blue, cursor: 'pointer', textDecoration: 'underline dotted' }}
                              onClick={() => openDrill(`Joined in ${row.label} — All (${row.totalJoined})`, getJoinedEmps(row.jm))}>
                              {row.totalJoined}
                            </span>
                          </td>
                          {showBreakdownCols && <>
                            {/* Net Joined (excl. CCD/Venus/Y-code) */}
                            <td style={TD('#f0fdf4')}>
                              <span style={{ fontWeight: 800, color: '#059669', cursor: 'pointer', textDecoration: 'underline dotted' }}
                                onClick={() => openDrill(`Net Joined in ${row.label} — excl. CCD/Venus/Y-code (${row.netJoined})`, getNetEmps(row.jm))}>
                                {row.netJoined}
                              </span>
                            </td>
                            {/* CCD/Venus Total (active + resigned) */}
                            <td style={TD('#fff7ed')}>
                              {row.totalCCDVenus > 0
                                ? <span style={{ fontWeight: 800, color: '#c2410c', cursor: 'pointer', textDecoration: 'underline dotted' }}
                                    onClick={() => openDrill(`CCD/Venus Joined in ${row.label} — Total (${row.totalCCDVenus})`, getCCDVenusEmps(row.jm))}>
                                    {row.totalCCDVenus}
                                  </span>
                                : <span style={{ color: '#e5e7eb', fontSize: 11 }}>—</span>}
                            </td>
                            {/* Y Code Total (active + resigned) */}
                            <td style={TD('#f5f3ff')}>
                              {row.totalYCode > 0
                                ? <span style={{ fontWeight: 800, color: '#7c3aed', cursor: 'pointer', textDecoration: 'underline dotted' }}
                                    onClick={() => openDrill(`Y-Code Joined in ${row.label} — Total (${row.totalYCode})`, getYCodeEmps(row.jm))}>
                                    {row.totalYCode}
                                  </span>
                                : <span style={{ color: '#e5e7eb', fontSize: 11 }}>—</span>}
                            </td>
                          </>}
                          {/* Resigned per month */}
                          {visibleResignMonths.map(m => {
                            const cnt        = row.perResign[m] || 0;
                            const isOwnMonth = m === row.jm;
                            const bg = isOwnMonth ? '#FEF9C3'
                                     : cnt > 0    ? '#fef2f2' : 'transparent';
                            return (
                              <td key={m} style={TD(bg)}>
                                {cnt > 0
                                  ? <span style={{ fontWeight: 800, cursor: 'pointer', textDecoration: 'underline dotted',
                                      color: isOwnMonth ? C.amber : C.red }}
                                      onClick={() => openDrill(`Resigned in ${dayjs(m, 'MMM YYYY').format("MMM'YY")} — joined ${row.label} (${cnt})`, getResignCellEmps(row.jm, m))}>
                                      {cnt}
                                    </span>
                                  : <span style={{ color: '#e5e7eb', fontSize: 11 }}>—</span>
                                }
                              </td>
                            );
                          })}
                          {/* Total resigned */}
                          <td style={TD('#fef2f2')}>
                            {row.totalResigned > 0
                              ? <span style={{ fontWeight: 800, color: C.red, cursor: 'pointer', textDecoration: 'underline dotted' }}
                                  onClick={() => openDrill(`All Resigned from ${row.label} cohort (${row.totalResigned})`, getTotalResignedEmps(row.jm))}>
                                  {row.totalResigned}
                                </span>
                              : <span style={{ color: '#d1d5db' }}>0</span>}
                          </td>
                          {/* Active */}
                          <td style={TD('#f0fdf4')}>
                            <span style={{ fontWeight: 800, color: C.green, cursor: 'pointer', textDecoration: 'underline dotted' }}
                              onClick={() => openDrill(`Active (Net) from ${row.label} cohort (${row.active})`, getActiveEmps(row.jm))}>
                              {row.active}
                            </span>
                          </td>
                          {/* % Loss */}
                          <td style={TD()}>
                            <span style={{ fontWeight: 800,
                              color: row.lossPct > 20 ? C.red : row.lossPct > 10 ? C.amber : C.green }}>
                              {row.lossPct}%
                            </span>
                          </td>
                          {showBreakdownCols && (
                            <td style={TD('#fffbeb')}>
                              <span style={{ fontWeight: 800,
                                color: row.netLossPct > 20 ? C.red : row.netLossPct > 10 ? C.amber : C.green }}>
                                {row.netLossPct}%
                              </span>
                            </td>
                          )}
                          {/* % Retain */}
                          <td style={{ ...TD(), ...(!showInfoCols && { borderRight: 'none' }) }}>
                            <span style={{ fontWeight: 800,
                              color: row.retainPct >= 90 ? C.green : row.retainPct >= 80 ? C.amber : C.red }}>
                              {row.retainPct}%
                            </span>
                          </td>
                          {showInfoCols && <>
                            {/* After 180 */}
                            <td style={TD('#f5f3ff')}>
                              {row.after180Resigned > 0
                                ? <span style={{ fontWeight: 800, color: '#7c3aed' }}>{row.after180Resigned}</span>
                                : <span style={{ color: '#e5e7eb', fontSize: 11 }}>—</span>}
                            </td>
                            {/* CCD/Venus */}
                            <td style={TD('#fff7ed')}>
                              {row.ccdVenusResigned > 0
                                ? <span style={{ fontWeight: 800, color: '#c2410c' }}>{row.ccdVenusResigned}</span>
                                : <span style={{ color: '#e5e7eb', fontSize: 11 }}>—</span>}
                            </td>
                            {/* Y Code */}
                            <td style={{ ...TD('#ecfdf5'), borderRight: 'none' }}>
                              {row.yCodeResigned > 0
                                ? <span style={{ fontWeight: 800, color: '#059669' }}>{row.yCodeResigned}</span>
                                : <span style={{ color: '#e5e7eb', fontSize: 11 }}>—</span>}
                            </td>
                          </>}
                        </tr>
                      ))}
                      {/* TOTAL row — EC (Dec'25+) sums to match visible rows */}
                      <tr style={{ background: C.navy, borderTop: `2px solid ${C.navy}` }}>
                        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 800,
                          color: '#fff', position: 'sticky', left: 0, background: C.navy,
                          borderRight: '1px solid rgba(255,255,255,0.15)' }}>TOTAL</td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                          fontSize: 14, color: '#93c5fd',
                          borderRight: '1px solid rgba(255,255,255,0.15)' }}>{ecTotal}</td>
                        {showBreakdownCols && <>
                          <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 14, color: '#6ee7b7',
                            borderRight: '1px solid rgba(255,255,255,0.15)' }}>{ecNetJoined}</td>
                          <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 14, color: '#fdba74',
                            borderRight: '1px solid rgba(255,255,255,0.15)' }}>{ecTotalCCDVenus}</td>
                          <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 14, color: '#c4b5fd',
                            borderRight: '1px solid rgba(255,255,255,0.15)' }}>{ecTotalYCode}</td>
                        </>}
                        {visibleResignMonths.map(m => (
                          <td key={m} style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 13, borderRight: '1px solid rgba(255,255,255,0.15)',
                            color: totPerMonth[m] > 0 ? '#fca5a5' : 'rgba(255,255,255,0.3)' }}>
                            {totPerMonth[m] || 0}
                          </td>
                        ))}
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                          fontSize: 14, color: '#fca5a5',
                          borderRight: '1px solid rgba(255,255,255,0.15)' }}>{ecResigned}</td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                          fontSize: 14, color: '#86efac',
                          borderRight: '1px solid rgba(255,255,255,0.15)' }}>{decPlusStats?.inJourney ?? 0}</td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                          fontSize: 14, color: '#fde68a',
                          borderRight: '1px solid rgba(255,255,255,0.15)' }}>{ecAttrPct}%</td>
                        {showBreakdownCols && (
                          <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 14, color: '#fcd34d',
                            borderRight: '1px solid rgba(255,255,255,0.15)' }}>{ecNetAttrPct}%</td>
                        )}
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                          fontSize: 14, color: '#86efac',
                          ...(!showInfoCols && { borderRight: 'none' }) }}>
                          {ecTotal > 0 ? Math.round(((decPlusStats?.inJourney ?? 0) / ecTotal) * 100) : 0}%
                        </td>
                        {showInfoCols && <>
                          <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 14, color: '#c4b5fd',
                            borderRight: '1px solid rgba(255,255,255,0.15)' }}>{ecAfter180}</td>
                          <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 14, color: '#fdba74',
                            borderRight: '1px solid rgba(255,255,255,0.15)' }}>{ecCcdVenus}</td>
                          <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 14, color: '#6ee7b7' }}>{ecYCode}</td>
                        </>}
                      </tr>
                    </tbody>
                    {/* Under 10 days footer */}
                    <tfoot>
                      <tr style={{ background: '#fff7ed', borderTop: `2px solid ${C.amber}55` }}>
                        <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700,
                          color: C.amber, position: 'sticky', left: 0, background: '#fff7ed',
                          borderRight: `1px solid ${C.border}` }}>
                          ⚡ Resigned &lt; 10 days
                        </td>
                        <td style={{ padding: '8px 10px', background: '#fff7ed',
                          borderRight: `1px solid ${C.border}` }} />
                        {showBreakdownCols && <>
                          <td style={{ padding: '8px 10px', background: '#fff7ed', borderRight: `1px solid ${C.border}` }} />
                          <td style={{ padding: '8px 10px', background: '#fff7ed', borderRight: `1px solid ${C.border}` }} />
                          <td style={{ padding: '8px 10px', background: '#fff7ed', borderRight: `1px solid ${C.border}` }} />
                        </>}
                        {visibleResignMonths.map(m => (
                          <td key={m} style={{ padding: '8px 10px', textAlign: 'center',
                            fontSize: 12, fontWeight: 700, background: '#fff7ed',
                            borderRight: `1px solid ${C.border}` }}>
                            {under10[m] > 0
                              ? <span style={{ fontWeight: 800, color: C.red, cursor: 'pointer', textDecoration: 'underline dotted' }}
                                  onClick={() => openDrill(`Resigned < 10 days in ${dayjs(m, 'MMM YYYY').format("MMM'YY")} (${under10[m]})`, getUnder10Emps(m))}>
                                  {under10[m]}
                                </span>
                              : <span style={{ color: '#d1d5db' }}>—</span>}
                          </td>
                        ))}
                        <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 800,
                          color: C.red, background: '#fff7ed',
                          borderRight: `1px solid ${C.border}` }}>{totUnder10}</td>
                        <td style={{ padding: '8px 10px', background: '#fff7ed',
                          borderRight: `1px solid ${C.border}` }} />
                        <td style={{ padding: '8px 10px', background: '#fff7ed',
                          borderRight: `1px solid ${C.border}` }} />
                        <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 800,
                          background: '#fff7ed', borderRight: (showInfoCols || showBreakdownCols) ? `1px solid ${C.border}` : 'none',
                          color: under10Pct > 30 ? C.red : under10Pct > 15 ? C.amber : C.green }}>
                          {under10Pct}%
                        </td>
                        {showBreakdownCols && (
                          <td style={{ padding: '8px 10px', background: '#fff7ed',
                            borderRight: showInfoCols ? `1px solid ${C.border}` : 'none' }} />
                        )}
                        {showInfoCols && <>
                          <td style={{ padding: '8px 10px', background: '#fff7ed',
                            borderRight: `1px solid ${C.border}` }} />
                          <td style={{ padding: '8px 10px', background: '#fff7ed',
                            borderRight: `1px solid ${C.border}` }} />
                          <td style={{ padding: '8px 10px', background: '#fff7ed', borderRight: 'none' }} />
                        </>}
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {/* Legend */}
                <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.border}`,
                  background: C.surf, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                  {[
                    { color: '#FEF9C3', border: C.amber, label: 'Resigned in own join month' },
                    { color: '#fef2f2', border: C.red,   label: 'Resigned in later month' },
                    { color: '#fff7ed', border: C.amber, label: '⚡ Under 10 days — critical early exit' },
                  ].map(({ color, border, label }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3,
                        background: color, border: `1.5px solid ${border}`, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: C.text3 }}>{label}</span>
                    </div>
                  ))}
                  <span style={{ fontSize: 11, color: C.text3, marginLeft: 'auto' }}>
                    💡 Click any count to see employees
                  </span>
                </div>
              </div>

              {/* Drill-down modal */}
              <Modal
                title={<span style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{drillDown?.title}</span>}
                open={!!drillDown}
                onCancel={() => setDrillDown(null)}
                footer={null}
                width={820}
                styles={{ body: { padding: '16px 0 0' } }}
              >
                {drillDown && (() => {
                  const emps = drillDown.emps;
                  return (
                    <>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: C.navy }}>
                              {['#', 'Code', 'Name', 'Division', 'Join Date', 'Resign Date', 'Tenure (days)'].map(h => (
                                <th key={h} style={{ padding: '9px 10px', color: '#fff', fontWeight: 700,
                                  textAlign: h === 'Name' || h === 'Division' ? 'left' : 'center',
                                  borderRight: '1px solid rgba(255,255,255,0.15)', whiteSpace: 'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {emps.length === 0 ? (
                              <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center',
                                color: '#9ca3af', fontSize: 13 }}>No employees found</td></tr>
                            ) : emps.map((e, i) => {
                              const doj    = e.dateOfJoining || e.cohortDate;
                              const tenure = doj && e.resignedDate
                                ? dayjs(e.resignedDate).diff(dayjs(doj), 'day') : null;
                              return (
                                <tr key={e.empCode || i} style={{
                                  background: i % 2 === 0 ? '#f9fafb' : '#fff',
                                  borderBottom: '1px solid #e5e7eb' }}>
                                  <td style={{ padding: '8px 10px', textAlign: 'center', color: '#9ca3af', fontWeight: 600 }}>{i + 1}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: C.navy }}>{e.empCode || '—'}</td>
                                  <td style={{ padding: '8px 10px', fontWeight: 600, color: '#111827' }}>{e.empName || e.name || '—'}</td>
                                  <td style={{ padding: '8px 10px', color: '#4b5563' }}>{e.division || '—'}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>{doj ? dayjs(doj).format('DD MMM YYYY') : '—'}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center',
                                    color: e.resignedDate ? C.red : C.green, fontWeight: 700 }}>
                                    {e.resignedDate ? dayjs(e.resignedDate).format('DD MMM YYYY') : 'Active'}
                                  </td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 800,
                                    color: tenure === null ? C.green : tenure <= 10 ? C.red : tenure <= 30 ? C.amber : '#4b5563' }}>
                                    {tenure !== null ? tenure : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ padding: '10px 16px', background: '#f3f4f6', borderTop: '1px solid #e5e7eb',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                          Total: <strong>{emps.length}</strong> employee{emps.length !== 1 ? 's' : ''}
                        </span>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>
                          Tenure: red ≤ 10 days · amber ≤ 30 days · — = active
                        </span>
                      </div>
                    </>
                  );
                })()}
              </Modal>
            </div>
          );
        })()}

        {/* ══ Progress Comparison ═════════════════════════════════════════ */}
        {ecProgressStats && (() => {
          const STATIC = { quit: 290, months: 8, qpm: 36.3 };
          const first  = ecProgressStats[0]; // Dec '25–Jul '26

          const change    = first?.hasData && first.qpm != null
            ? Math.round((first.qpm - STATIC.qpm) * 10) / 10 : null;
          const pctChange = change != null
            ? Math.round((change / STATIC.qpm) * 1000) / 10 : null;
          const improved  = change != null && change < 0;

          const thSt = { padding: '10px 14px', color: '#fff', fontWeight: 700, whiteSpace: 'nowrap', fontSize: 12 };
          const tdS  = (align = 'center') => ({
            padding: '10px 14px', textAlign: align, fontSize: 12,
            borderBottom: '1px solid #e2e8f0', color: '#1a2d4a',
          });

          return (
            <div style={{ marginTop: 24 }}>
              <div style={{ borderRadius: 14, border: '1.5px solid #dce6f0', overflow: 'hidden', background: '#fff' }}>

                {/* Section header */}
                <div style={{ background: '#1B2D6B', padding: '13px 18px' }}>
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>How Eris Connect Is Progressing</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginTop: 3 }}>
                    Comparing early joiners (Apr–Nov '25) vs Connect cohorts (Dec '25 onwards) &middot; 6-month resignation benchmark
                  </div>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#1B2D6B' }}>
                        <th style={{ ...thSt, textAlign: 'left', minWidth: 300, paddingTop: 8 }}>Metric</th>
                        <th style={{ ...thSt, textAlign: 'center', color: '#93c5fd' }}>Joined Apr–Nov '25</th>
                        {ecProgressStats.map(b => (
                          <th key={b.label} style={{ ...thSt, textAlign: 'center', color: '#6ee7b7' }}>
                            Joined {b.label}
                          </th>
                        ))}
                        <th style={{ ...thSt, textAlign: 'center', color: '#fde68a' }}>Change</th>
                        <th style={{ ...thSt, textAlign: 'center', color: '#fde68a' }}>% Change</th>
                        <th style={{ ...thSt, textAlign: 'center', color: '#6ee7b7' }}>Improved?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Note row */}
                      <tr style={{ background: '#f8fafc' }}>
                        <td colSpan={4 + ecProgressStats.length} style={{ padding: '7px 14px', color: '#4a6580', fontStyle: 'italic', fontSize: 11, borderBottom: '1px solid #e2e8f0' }}>
                          Counting only people who have already been here long enough to reach the 6-month mark (fair comparison)
                        </td>
                      </tr>

                      {/* Row 1: Quit before 6 months */}
                      <tr style={{ background: '#fff' }}>
                        <td style={{ ...tdS('left'), fontWeight: 500 }}>People who quit before finishing 6 months</td>
                        <td style={{ ...tdS(), fontWeight: 700, color: '#1E6FBF' }}>{STATIC.quit}</td>
                        {ecProgressStats.map(b => (
                          <td key={b.label} style={{ ...tdS(), fontWeight: 700, color: b.hasData ? '#16a34a' : '#9ca3af' }}>
                            {b.hasData ? b.quit : '—'}
                          </td>
                        ))}
                        <td style={{ ...tdS(), background: '#f8fafc' }} />
                        <td style={{ ...tdS(), background: '#f8fafc' }} />
                        <td style={{ ...tdS(), background: '#f8fafc' }} />
                      </tr>

                      {/* Row 2: Hiring months */}
                      <tr style={{ background: '#fafcff' }}>
                        <td style={{ ...tdS('left'), fontWeight: 500 }}>Hiring months counted</td>
                        <td style={{ ...tdS(), fontWeight: 700, color: '#1E6FBF' }}>{STATIC.months}</td>
                        {ecProgressStats.map(b => (
                          <td key={b.label} style={{ ...tdS(), fontWeight: 700, color: b.hasData ? '#16a34a' : '#9ca3af' }}>
                            {b.hasData ? b.hiringMonths : '—'}
                          </td>
                        ))}
                        <td style={{ ...tdS(), background: '#f8fafc' }} />
                        <td style={{ ...tdS(), background: '#f8fafc' }} />
                        <td style={{ ...tdS(), background: '#f8fafc' }} />
                      </tr>

                      {/* Row 3: KEY — quitters per month */}
                      <tr style={{ background: improved ? '#f0fdf4' : '#fff' }}>
                        <td style={{ ...tdS('left'), fontWeight: 700 }}>Resigned for every month of hiring</td>
                        <td style={{ ...tdS(), fontWeight: 800, color: '#dc2626', fontSize: 14 }}>{STATIC.qpm}</td>
                        {ecProgressStats.map(b => (
                          <td key={b.label} style={{
                            ...tdS(), fontWeight: 800, fontSize: b.hasData ? 14 : 12,
                            color: b.hasData ? (b.qpm < STATIC.qpm ? '#16a34a' : '#dc2626') : '#9ca3af',
                          }}>
                            {b.hasData && b.qpm != null ? b.qpm : '—'}
                          </td>
                        ))}
                        <td style={{ ...tdS(), fontWeight: 700, color: change != null ? (change < 0 ? '#16a34a' : '#dc2626') : '#9ca3af' }}>
                          {change != null ? (change > 0 ? '+' : '') + change : '—'}
                        </td>
                        <td style={{ ...tdS(), fontWeight: 700, color: pctChange != null ? (pctChange < 0 ? '#16a34a' : '#dc2626') : '#9ca3af' }}>
                          {pctChange != null ? (pctChange > 0 ? '+' : '') + pctChange + '%' : '—'}
                        </td>
                        <td style={{ ...tdS(), fontWeight: 700, color: improved ? '#16a34a' : change != null ? '#dc2626' : '#9ca3af' }}>
                          {improved ? '✅ Improved' : change != null ? '❌ Not improved' : '—'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div style={{ padding: '7px 14px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: 10, color: '#7a96b0' }}>
                  * Change and % Change compare {ecProgressStats[0]?.label ?? "Dec '25 onwards"} vs Apr–Nov '25. Future brackets will populate as employees complete 6 months.
                </div>
              </div>
            </div>
          );
        })()}

        {/* ══ EARLY LOSS (within 6 months) ════════════════════════ */}
        {clusterConfig && employees.length > 0 && (() => {
          const { clusters = [], aliases = [] } = clusterConfig;

          // Hardcoded fallback aliases — active regardless of DB state
          const FALLBACK_ALIASES = {
            'btrc':                           'biocon transplant and renal care',
            'biocon tx and renal care':       'biocon transplant and renal care',
            'biocon tx & renal care':         'biocon transplant and renal care',
            'biocon transplant & renal care': 'biocon transplant and renal care',
            'onco':                           'oncology',
            'biocon oncology':                'oncology',
            'biocon onco':                    'oncology',
          };

          // Alias map: lowercase alias → lowercase canonical division name
          // Handles both camelCase (canonicalName) and snake_case (canonical_name) API responses
          const aliasMap = { ...FALLBACK_ALIASES };
          aliases.forEach(a => {
            const aliasKey  = (a.alias || '').toLowerCase().trim();
            const canonical = ((a.canonicalName || a.canonical_name) || '').toLowerCase().trim();
            // FALLBACK_ALIASES are authoritative — DB entries cannot overwrite them
            if (aliasKey && canonical && !FALLBACK_ALIASES[aliasKey]) aliasMap[aliasKey] = canonical;
          });

          const resolveDiv = name => {
            const lower = (name || '').toLowerCase().trim();
            return aliasMap[lower] || lower;
          };

          // Reverse alias map: lowercase canonical → array of lowercase aliases
          // Used for static data lookup (e.g., 'biocon transplant and renal care' → ['btrc', ...])
          const canonToAliases = {};
          Object.entries(aliasMap).forEach(([aliasKey, canon]) => {
            if (!canonToAliases[canon]) canonToAliases[canon] = [];
            canonToAliases[canon].push(aliasKey);
          });

          // Skip super-clusters that are aggregates of other clusters
          // (identified by having no unique divisions — all their div names resolve to
          //  the same canonicals as divisions already in other clusters)
          // Simple heuristic: exclude clusters whose title contains common super-cluster keywords
          const SUPER_TITLES = new Set([
            'cardiometabolic', 'one diabetes & cardio',
            'derma, cns & acute business',
            'mkt access, oncology, corporate hospital, nephrology & hospital bu',
          ]);
          const isSuper = c => SUPER_TITLES.has(c.title.toLowerCase().trim());

          // Leaf clusters only, sorted by sequenceNo
          const leafClusters = clusters
            .filter(c => c.isActive && !c.isDeleted && !isSuper(c))
            .sort((a, b) => (a.sequenceNo || 0) - (b.sequenceNo || 0));

          // Build display structure: cluster → unique divisions (deduped by resolved key)
          const seenDivKeys = new Set();
          const displayClusters = leafClusters.map(c => {
            const divs = (c.divisions || [])
              .filter(d => d.isActive && !d.isDeleted)
              .reduce((acc, d) => {
                const key = resolveDiv(d.divisionName);
                if (!seenDivKeys.has(key)) {
                  seenDivKeys.add(key);
                  acc.push({ key, label: d.divisionName });
                }
                return acc;
              }, []);
            return { id: c.id, title: c.title, seq: c.sequenceNo, divisions: divs };
          }).filter(c => c.divisions.length > 0);

          // Quarters
          const STATIC_QTRS = [
            { label: 'Apr-Jun 25', from: '1-Apr-25', to: '30-Jun-25', months: 3 },
            { label: 'Jul-Sep 25', from: '1-Jul-25', to: '30-Sep-25', months: 3 },
          ];
          const EC_QTRS = [
            { label: 'Oct-Dec 25', start: '2025-10-01', end: '2025-12-31', from: '1-Oct-25', to: '31-Dec-25', months: 3 },
            { label: 'Jan-Mar 26', start: '2026-01-01', end: '2026-03-31', from: '1-Jan-26', to: '31-Mar-26', months: 3 },
            { label: 'Apr-Jun 26', start: '2026-04-01', end: '2026-06-30', from: '1-Apr-26', to: '30-Jun-26', months: 3 },
            { label: 'Jul-Sep 26', start: '2026-07-01', end: '2026-09-30', from: '1-Jul-26', to: '30-Sep-26', months: 3 },
          ];

          // Static pre-Connect data (keys are lowercase short-form division names from Excel)
          const STATIC_DATA = {
            'Apr-Jun 25': { eris:6, aspire:14, victus:1, phoenix:5, adura:1, inspira:3, 'blu team':8,
              cosmecare:4, dermacare:3, skincare:3, 'biocon immuno':4, altiza:4, eterna:1,
              nikkos:5, rise:51, filix:3, strides:5, montana:5, 'bio art':9, btrc:2, oncology:2 },
            'Jul-Sep 25': { eris:7, aspire:7, victus:9, phoenix:7, adura:10, inspira:4, 'blu team':6,
              cosmecare:3, dermacare:2, skincare:5, 'biocon immuno':3, altiza:2, eterna:1,
              nikkos:10, rise:17, filix:4, strides:1, montana:6, 'bio art':3, btrc:2 },
          };

          // Static lookup: try canonical key first, then all known aliases of that canonical
          const getStaticVal = (divKey, qLabel) => {
            let v = STATIC_DATA[qLabel]?.[divKey] || 0;
            if (v) return v;
            for (const alias of (canonToAliases[divKey] || [])) {
              v = STATIC_DATA[qLabel]?.[alias] || 0;
              if (v) return v;
            }
            return 0;
          };

          // Dynamic map from EC employees — step 1: collect raw lowercase counts
          const rawDynMap = {};
          employees.forEach(e => {
            if (!e.isResigned || !e.resignedDate || !e.dateOfJoining) return;
            const dojD = dayjs(e.dateOfJoining);
            const rdD  = dayjs(e.resignedDate);
            if (!dojD.isValid() || !rdD.isValid()) return;
            if (rdD.diff(dojD, 'day') > 180) return;
            if ((e.empCode || '').toUpperCase().startsWith('Y')) return;
            const rawKey = (e.division || '').toLowerCase().trim();
            if (!rawKey) return;
            EC_QTRS.forEach(q => {
              if (dojD.isBefore(dayjs(q.start)) || dojD.isAfter(dayjs(q.end))) return;
              if (!rawDynMap[rawKey]) rawDynMap[rawKey] = {};
              rawDynMap[rawKey][q.label] = (rawDynMap[rawKey][q.label] || 0) + 1;
            });
          });

          // Step 2: deep-copy rawDynMap, then merge alias counts into their canonical key.
          // Deep copy prevents mutation of rawDynMap when we update canonical entries.
          // Self-alias guard prevents doubling when aliasKey === canonKey.
          const dynMap = {};
          Object.entries(rawDynMap).forEach(([k, v]) => { dynMap[k] = { ...v }; });
          Object.entries(aliasMap).forEach(([aliasKey, canonKey]) => {
            if (aliasKey === canonKey) return;           // skip self-aliases
            if (!rawDynMap[aliasKey]) return;            // no employees under this alias
            if (!dynMap[canonKey]) dynMap[canonKey] = {};
            Object.entries(rawDynMap[aliasKey]).forEach(([qLabel, n]) => {
              dynMap[canonKey][qLabel] = (dynMap[canonKey][qLabel] || 0) + n;
            });
          });

          // Simple direct lookup: cluster divisions always use canonical keys
          const getDynVal = (canonicalKey, qLabel) => dynMap[canonicalKey]?.[qLabel] || 0;

          const clusterStaticTotal = (cluster, qLabel) =>
            cluster.divisions.reduce((s, d) => s + getStaticVal(d.key, qLabel), 0);
          const clusterDynTotal = (cluster, qLabel) =>
            cluster.divisions.reduce((s, d) => s + getDynVal(d.key, qLabel), 0);
          const grandStaticTotal = qLabel =>
            displayClusters.reduce((s, c) => s + clusterStaticTotal(c, qLabel), 0);
          const grandDynTotal = qLabel =>
            displayClusters.reduce((s, c) => s + clusterDynTotal(c, qLabel), 0);

          const thBase = { padding: '6px 10px', textAlign: 'center', fontSize: 11,
            fontWeight: 700, minWidth: 90, whiteSpace: 'nowrap', borderBottom: '2px solid #e2e8f0' };

          return (
            <div style={{ marginTop: 24, marginBottom: 8 }}>
              <div style={{ borderRadius: 14, border: '1.5px solid #dce6f0', overflow: 'hidden', background: '#fff' }}>
                <div style={{ background: C.navy, padding: '13px 18px' }}>
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>Early Loss (within 6 months)</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginTop: 3 }}>
                    Employees who resigned within 6 months of joining · Pre-Connect: fixed data · Connect: live from EC employees
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                    <thead>
                      <tr>
                        <th rowSpan={2} style={{ padding: '8px 12px', textAlign: 'left',
                          background: '#1a2d4a', color: '#fff', fontWeight: 700, fontSize: 12,
                          minWidth: 210, borderRight: '2px solid rgba(255,255,255,0.2)',
                          borderBottom: '1px solid #e2e8f0', position: 'sticky', left: 0, zIndex: 3 }}>
                          Phase
                          <br />
                          <span style={{ fontWeight: 400, fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>
                            Cluster / Division
                          </span>
                        </th>
                        <th colSpan={STATIC_QTRS.length} style={{ padding: '7px 12px', textAlign: 'center',
                          background: '#f9c201', color: '#1a2d4a', fontWeight: 800, fontSize: 12,
                          borderRight: '3px solid #1a2d4a', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                          Pre Connect
                        </th>
                        <th colSpan={EC_QTRS.length} style={{ padding: '7px 12px', textAlign: 'center',
                          background: '#f9c201', color: '#1a2d4a', fontWeight: 800, fontSize: 12,
                          borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                          Connect
                        </th>
                      </tr>
                      <tr>
                        {STATIC_QTRS.map((q, i) => (
                          <th key={q.label} style={{ ...thBase, background: '#fef3c7', color: '#92400e',
                            borderRight: i === STATIC_QTRS.length - 1 ? '3px solid #1a2d4a' : '1px solid #e2e8f0' }}>
                            {q.label}
                          </th>
                        ))}
                        {EC_QTRS.map((q, i) => (
                          <th key={q.label} style={{ ...thBase, background: '#dcfce7', color: '#166534',
                            borderRight: i < EC_QTRS.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                            {q.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayClusters.map(cluster => (
                        <React.Fragment key={cluster.id}>
                          {cluster.divisions.map(div => (
                            <tr key={div.key}>
                              <td style={{ padding: '5px 12px 5px 22px', fontSize: 12, color: '#1a2d4a',
                                borderRight: '2px solid #e2e8f0', borderBottom: '1px solid #edf2f7',
                                position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                                {div.label}
                              </td>
                              {STATIC_QTRS.map((q, i) => {
                                const v = getStaticVal(div.key, q.label);
                                return (
                                  <td key={q.label} style={{ padding: '5px 10px', textAlign: 'center',
                                    fontSize: 12, background: '#fff',
                                    color: v > 0 ? '#1a2d4a' : '#d1d5db',
                                    borderRight: i === STATIC_QTRS.length - 1 ? '3px solid #1a2d4a' : '1px solid #edf2f7',
                                    borderBottom: '1px solid #edf2f7' }}>
                                    {v > 0 ? v : <span style={{ color: '#cbd5e1' }}>-</span>}
                                  </td>
                                );
                              })}
                              {EC_QTRS.map((q, i) => {
                                const v = getDynVal(div.key, q.label);
                                return (
                                  <td key={q.label} style={{ padding: '5px 10px', textAlign: 'center',
                                    fontSize: 12, background: '#fff',
                                    color: v > 0 ? '#1a2d4a' : '#d1d5db',
                                    borderRight: i < EC_QTRS.length - 1 ? '1px solid #edf2f7' : 'none',
                                    borderBottom: '1px solid #edf2f7' }}>
                                    {v > 0 ? v : <span style={{ color: '#cbd5e1' }}>-</span>}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                          {/* Cluster total row */}
                          <tr style={{ background: '#f0f5ff' }}>
                            <td style={{ padding: '6px 12px', fontSize: 12, fontWeight: 800,
                              color: '#1e40af', borderRight: '2px solid #dbeafe',
                              borderBottom: '2px solid #dbeafe',
                              position: 'sticky', left: 0, background: '#f0f5ff', zIndex: 1 }}>
                              {cluster.title}
                            </td>
                            {STATIC_QTRS.map((q, i) => {
                              const v = clusterStaticTotal(cluster, q.label);
                              return (
                                <td key={q.label} style={{ padding: '6px 10px', textAlign: 'center',
                                  fontSize: 12, fontWeight: 700, background: '#f0f5ff',
                                  color: v > 0 ? '#dc2626' : '#d1d5db',
                                  borderRight: i === STATIC_QTRS.length - 1 ? '3px solid #1a2d4a' : '1px solid #dbeafe',
                                  borderBottom: '2px solid #dbeafe' }}>
                                  {v > 0 ? v : <span style={{ color: '#cbd5e1' }}>-</span>}
                                </td>
                              );
                            })}
                            {EC_QTRS.map((q, i) => {
                              const v = clusterDynTotal(cluster, q.label);
                              return (
                                <td key={q.label} style={{ padding: '6px 10px', textAlign: 'center',
                                  fontSize: 12, fontWeight: 700, background: '#f0f5ff',
                                  color: v > 0 ? '#dc2626' : '#d1d5db',
                                  borderRight: i < EC_QTRS.length - 1 ? '1px solid #dbeafe' : 'none',
                                  borderBottom: '2px solid #dbeafe' }}>
                                  {v > 0 ? v : <span style={{ color: '#cbd5e1' }}>-</span>}
                                </td>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      ))}

                      {/* Other / Unmapped — divisions not yet in cluster config */}
                      {/* Grand Total */}
                      <tr style={{ background: '#1a2d4a' }}>
                        <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 900, color: '#fff',
                          borderRight: '2px solid rgba(255,255,255,0.2)',
                          position: 'sticky', left: 0, background: '#1a2d4a', zIndex: 1 }}>
                          Total
                        </td>
                        {STATIC_QTRS.map((q, i) => (
                          <td key={q.label} style={{ padding: '8px 10px', textAlign: 'center',
                            fontSize: 13, fontWeight: 900, color: '#fde68a',
                            borderRight: i === STATIC_QTRS.length - 1
                              ? '3px solid rgba(255,255,255,0.25)' : '1px solid rgba(255,255,255,0.15)' }}>
                            {grandStaticTotal(q.label)}
                          </td>
                        ))}
                        {EC_QTRS.map((q, i) => (
                          <td key={q.label} style={{ padding: '8px 10px', textAlign: 'center',
                            fontSize: 13, fontWeight: 900, color: '#86efac',
                            borderRight: i < EC_QTRS.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none' }}>
                            {grandDynTotal(q.label)}
                          </td>
                        ))}
                      </tr>

                      {/* Footer rows: Avg per Month, Period From, Period To, No. of Months */}
                      {[
                        {
                          label: 'Avg per Month', topBorder: true,
                          renderStatic: q => {
                            const avg = Math.round((grandStaticTotal(q.label) / q.months) * 10) / 10;
                            return avg;
                          },
                          renderDyn: q => {
                            const v = grandDynTotal(q.label);
                            return v > 0 ? Math.round((v / q.months) * 10) / 10 : <span style={{ color: '#cbd5e1' }}>-</span>;
                          },
                          color: '#4a6580',
                        },
                        { label: 'Period From', renderStatic: q => q.from, renderDyn: q => q.from, color: '#1e6fbf' },
                        { label: 'Period To',   renderStatic: q => q.to,   renderDyn: q => q.to,   color: '#1e6fbf' },
                        { label: 'No. of Months', renderStatic: q => q.months, renderDyn: q => q.months, color: '#4a6580' },
                      ].map(row => (
                        <tr key={row.label} style={{ background: '#f8fafc' }}>
                          <td style={{ padding: '5px 12px', fontSize: 11, fontWeight: 600, color: row.color,
                            borderRight: '2px solid #e2e8f0',
                            borderTop: row.topBorder ? '2px solid #e2e8f0' : 'none',
                            position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1 }}>
                            {row.label}
                          </td>
                          {STATIC_QTRS.map((q, i) => (
                            <td key={q.label} style={{ padding: '5px 10px', textAlign: 'center',
                              fontSize: 11, fontWeight: 600, color: row.color,
                              borderRight: i === STATIC_QTRS.length - 1 ? '3px solid #1a2d4a' : '1px solid #e2e8f0',
                              borderTop: row.topBorder ? '2px solid #e2e8f0' : 'none' }}>
                              {row.renderStatic(q)}
                            </td>
                          ))}
                          {EC_QTRS.map((q, i) => (
                            <td key={q.label} style={{ padding: '5px 10px', textAlign: 'center',
                              fontSize: 11, fontWeight: 600, color: row.color,
                              borderRight: i < EC_QTRS.length - 1 ? '1px solid #e2e8f0' : 'none',
                              borderTop: row.topBorder ? '2px solid #e2e8f0' : 'none' }}>
                              {row.renderDyn(q)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

              </div>
            </div>
          );
        })()}

        {/* ══ MONTH-WISE TENURE ATTRITION ══════════════════════ */}
        {(() => {
          const BUCKET_COLORS = {
            '0-2 Months':'#dc2626','2-4 Months':'#b45309',
            '4-6 Months':'#1E6FBF','6-12 Months':'#16a34a','12+ Months':'#7c3aed',
          };
          const fmtMonth = raw => {
            if (!raw) return '—';
            const m1 = raw.match(/^(\d{2})-(\w{3})$/);
            if (m1) return `${m1[2]}, ${2000+parseInt(m1[1],10)}`;
            const m2 = raw.match(/^(\w{3})[,\s]+(\d{4})$/);
            if (m2) return `${m2[1]}, ${m2[2]}`;
            return raw;
          };
          // Recompute from EC-Status-included employees (Dec'25+, no After180/CCD-Venus/YCode)
          const CLOSED_T = new Set(['ccd', 'venus']);
          const MAY_T    = dayjs('2026-05-01').valueOf();
          const DEC_T    = dayjs('2025-12-01').valueOf();
          const getTenureBkt = (doj, rd) => {
            if (!doj || !rd) return null;
            const d = dayjs(rd).diff(dayjs(doj), 'day');
            if (d <= 10)  return '0-10 Days';
            if (d <= 30)  return '10 Days-1 Month';
            if (d <= 60)  return '1-2 Months';
            if (d <= 90)  return '2-3 Months';
            if (d <= 120) return '3-4 Months';
            if (d <= 150) return '4-5 Months';
            if (d <= 180) return '5-6 Months';
            return '6+ Months';
          };
          const filtResignedT = (attrStats?.resignedList || []).filter(e => {
            const doj = e.dateOfJoining || e.cohortDate;
            const div = (e.division || '').trim().toLowerCase();
            if (!doj || dayjs(doj).valueOf() < DEC_T) return false;
            if (CLOSED_T.has(div) && dayjs(doj).valueOf() > DEC_T &&
                e.resignedDate && dayjs(e.resignedDate).valueOf() >= MAY_T) return false;
            if (doj && e.resignedDate && dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) return false;
            if ((e.empCode || '').toUpperCase().startsWith('Y')) return false;
            return true;
          });
          const filtMonthMap = {};
          filtResignedT.forEach(e => {
            if (!e.resignedDate) return;
            const rm = dayjs(e.resignedDate).format('YY-MMM');
            if (!filtMonthMap[rm]) filtMonthMap[rm] = { totalResigned: 0, buckets: {} };
            filtMonthMap[rm].totalResigned += 1;
            const bkt = getTenureBkt(e.dateOfJoining || e.cohortDate, e.resignedDate);
            if (bkt) filtMonthMap[rm].buckets[bkt] = (filtMonthMap[rm].buckets[bkt] || 0) + 1;
          });
          const allMonthData = (attrStats?.monthTenure || []).map(m => {
            const key = m.month || m.division || m.cohort || '';
            const ft  = filtMonthMap[key] || { totalResigned: 0, buckets: {} };
            return { ...m, totalResigned: ft.totalResigned, buckets: ft.buckets };
          });
          const buckets = attrStats?.tenureBuckets || Object.keys(BUCKET_COLORS);
          // Split into Dec'25+ (main) and pre-Dec'25 (historical)
          const parseTenureMonth = raw => {
            if (!raw) return null;
            const m1 = raw.match(/^(\d{2})-(\w{3})$/);
            if (m1) return dayjs(`20${m1[1]}-${m1[2]}-01`, 'YYYY-MMM-DD');
            const m2 = raw.match(/^(\w{3})[,\s]+(\d{4})$/);
            if (m2) return dayjs(`${m2[2]}-${m2[1]}-01`, 'YYYY-MMM-DD');
            return null;
          };
          const DEC_2025_MS_T = dayjs('2025-12-01').valueOf();
          const monthData    = allMonthData.filter(r => {
            const d = parseTenureMonth(r.month || r.division || r.cohort || '');
            return !d || d.valueOf() >= DEC_2025_MS_T;
          });
          const histMonthData = allMonthData.filter(r => {
            const d = parseTenureMonth(r.month || r.division || r.cohort || '');
            return d && d.valueOf() < DEC_2025_MS_T;
          });
          const grandTotal  = monthData.reduce((s,r)=>s+(r.totalResigned||0),0);
          const histGrandTotal = histMonthData.reduce((s,r)=>s+(r.totalResigned||0),0);

          return (
            <div style={{marginBottom:20}}>
              {/* Section header */}
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,flexWrap:'wrap'}}>
                <span style={{fontSize:18}}>📅</span>
                <span style={{fontSize:15,fontWeight:700,color:C.text1}}>
                  Month-wise × Tenure Attrition
                </span>
                <span style={{fontSize:11,color:C.text3,background:'#eaf1f8',
                  padding:'2px 8px',borderRadius:20}}>
                  When do employees tend to resign?
                </span>
              </div>

              {/* Oct'25 · Nov'25 historical tenure section */}
              {showHistorical && histMonthData.length > 0 && (
                <div style={{ marginBottom: 14, borderRadius: 12, overflow: 'hidden',
                  border: '1.5px dashed #7c3aed' }}>
                  <div style={{ background: '#7c3aed', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>📋 Oct '25 · Nov '25 — Tenure Attrition (Historical)</span>
                    <span style={{ color: '#ddd6fe', fontSize: 11 }}>for reference only</span>
                  </div>
                  <div style={{ overflowX: 'auto', background: '#faf5ff' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                      <thead>
                        <tr style={{ background: '#6d28d9' }}>
                          <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#fff', minWidth: 140, position: 'sticky', left: 0, background: '#6d28d9', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Resign Month</th>
                          <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#fca5a5', borderLeft: '1px solid rgba(255,255,255,0.2)' }}>Resigned<br/><span style={{ fontSize: 10, opacity: .7 }}>(Attrition%)</span></th>
                          {buckets.map(b => (
                            <th key={b} colSpan={2} style={{ padding: '8px 10px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: BUCKET_COLORS[b] || '#fff', background: '#6d28d9', borderLeft: '1px solid rgba(255,255,255,0.2)', minWidth: 110 }}>{b}</th>
                          ))}
                        </tr>
                        <tr style={{ background: '#ede9fe' }}>
                          <td style={{ padding: '4px 14px', position: 'sticky', left: 0, background: '#ede9fe', borderRight: `1px solid ${C.border}` }} />
                          <td style={{ padding: '4px 10px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: C.red, borderLeft: `1px solid ${C.border}` }}># Attri</td>
                          {buckets.map(b => (
                            <React.Fragment key={b}>
                              <td style={{ padding: '4px 8px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: BUCKET_COLORS[b], borderLeft: `1px solid ${C.border}` }}># Attri</td>
                              <td style={{ padding: '4px 8px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: BUCKET_COLORS[b] }}>CT%</td>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {histMonthData.map((row, i) => {
                          const name = fmtMonth(row.month || row.division || row.cohort || '—');
                          const resigned = row.totalResigned || 0;
                          const attrPct = histGrandTotal > 0 ? Math.round((resigned / histGrandTotal) * 100) : 0;
                          return (
                            <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? '#f5f3ff' : '#fff' }}>
                              <td style={{ padding: '8px 14px', fontSize: 13, fontWeight: 700, color: '#7c3aed', position: 'sticky', left: 0, background: i % 2 === 0 ? '#f5f3ff' : '#fff', borderRight: `1px solid ${C.border}` }}>{name}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', borderLeft: `1px solid ${C.border}` }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                  <span style={{ fontWeight: 800, fontSize: 14, color: attrPct > 30 ? C.red : attrPct > 15 ? C.amber : C.green }}>{resigned}</span>
                                  <span style={{ fontSize: 10, color: C.text3, fontWeight: 600 }}>{attrPct}%</span>
                                </div>
                              </td>
                              {buckets.map(b => {
                                const raw = (row.buckets || {})[b]; const cnt = typeof raw === 'object' && raw !== null ? (raw.count || 0) : (raw || 0);
                                const ctPct = resigned > 0 ? Math.round((cnt / resigned) * 100) : 0;
                                const bcol = BUCKET_COLORS[b] || C.blue;
                                return (
                                  <React.Fragment key={b}>
                                    <td style={{ padding: '8px 8px', textAlign: 'center', borderLeft: `1px solid ${C.border}` }}>
                                      {cnt > 0 ? <span style={{ fontWeight: 700, fontSize: 13, color: bcol }}>{cnt}</span> : <span style={{ color: '#e2e8f0', fontSize: 11 }}>—</span>}
                                    </td>
                                    <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                                      {cnt > 0 ? <span style={{ fontSize: 11, color: C.text3, fontWeight: 600 }}>{ctPct}%</span> : <span style={{ color: '#e2e8f0', fontSize: 11 }}>—</span>}
                                    </td>
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {attrLoading
                ? <div style={{textAlign:'center',padding:32}}><Spin/></div>
                : !monthData.length
                  ? <div style={{textAlign:'center',padding:32,color:C.text3,
                      background:C.white,borderRadius:14,border:`1px solid ${C.border}`}}>
                      No resignation data available
                    </div>
                  : (
                    <div style={{background:C.white,borderRadius:14,
                      border:`1px solid ${C.border}`,overflow:'hidden',
                      boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
                      <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%',borderCollapse:'collapse',minWidth:600}}>
                          <thead>
                            {/* Row 1 — column group headers */}
                            <tr style={{background:C.navy}}>
                              <th style={{padding:'10px 16px',textAlign:'left',fontSize:12,
                                fontWeight:700,color:'#fff',minWidth:140,
                                position:'sticky',left:0,background:C.navy,
                                borderRight:'1px solid rgba(255,255,255,0.15)'}}>
                                Resign Month
                              </th>
                              <th style={{padding:'10px 12px',textAlign:'center',fontSize:11,
                                fontWeight:700,color:'#fca5a5',minWidth:90,
                                borderLeft:'1px solid rgba(255,255,255,0.15)'}}>
                                Resigned<br/><span style={{fontSize:10,opacity:.7}}>(Attrition%)</span>
                              </th>
                              {buckets.map(b=>(
                                <th key={b} colSpan={2} style={{padding:'10px 12px',
                                  textAlign:'center',fontSize:11,fontWeight:700,
                                  color:BUCKET_COLORS[b]||'#fff',background:C.navy,
                                  borderLeft:'1px solid rgba(255,255,255,0.1)',minWidth:130}}>
                                  {b}
                                </th>
                              ))}
                            </tr>
                            {/* Row 2 — sub-column labels */}
                            <tr style={{background:'#eaf1f8'}}>
                              <td style={{padding:'5px 16px',fontSize:11,color:C.text3,
                                position:'sticky',left:0,background:'#eaf1f8',
                                borderRight:`1px solid ${C.border}`}}/>
                              <td style={{padding:'5px 12px',textAlign:'center',
                                fontSize:10,fontWeight:700,color:C.red,
                                borderLeft:`1px solid ${C.border}`}}># Attri</td>
                              {buckets.map(b=>(
                                <React.Fragment key={b}>
                                  <td style={{padding:'5px 8px',textAlign:'center',
                                    fontSize:10,fontWeight:700,
                                    color:BUCKET_COLORS[b],
                                    borderLeft:`1px solid ${C.border}`}}># Attri</td>
                                  <td style={{padding:'5px 8px',textAlign:'center',
                                    fontSize:10,fontWeight:700,
                                    color:BUCKET_COLORS[b]}}>CT%</td>
                                </React.Fragment>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {monthData.map((row,i)=>{
                              const name     = fmtMonth(row.month||row.division||row.cohort||'—');
                              const resigned = row.totalResigned||0;
                              const attrPct  = grandTotal>0?Math.round((resigned/grandTotal)*100):0;
                              return (
                                <tr key={i} style={{borderBottom:`1px solid ${C.border}`,
                                  background:i%2===0?C.white:'#fafcff'}}>
                                  {/* Month name */}
                                  <td style={{padding:'9px 16px',fontSize:13,fontWeight:700,
                                    color:C.text1,position:'sticky',left:0,
                                    background:i%2===0?C.white:'#fafcff',
                                    borderRight:`1px solid ${C.border}`}}>
                                    {name}
                                  </td>
                                  {/* Total resigned */}
                                  <td style={{padding:'9px 12px',textAlign:'center',
                                    borderLeft:`1px solid ${C.border}`}}>
                                    <div style={{display:'flex',flexDirection:'column',
                                      alignItems:'center',gap:2}}>
                                      <span style={{fontWeight:800,fontSize:15,
                                        color:attrPct>30?C.red:attrPct>15?C.amber:C.green}}>
                                        {resigned}
                                      </span>
                                      <span style={{fontSize:10,color:C.text3,fontWeight:600}}>
                                        {attrPct}%
                                      </span>
                                    </div>
                                  </td>
                                  {/* Bucket cells */}
                                  {buckets.map(b=>{
                                    const raw  = (row.buckets||{})[b]; const cnt = typeof raw==='object'&&raw!==null ? (raw.count||0) : (raw||0);
                                    const ctPct= resigned>0?Math.round((cnt/resigned)*100):0;
                                    const bcol = BUCKET_COLORS[b]||C.blue;
                                    return (
                                      <React.Fragment key={b}>
                                        <td style={{padding:'9px 8px',textAlign:'center',
                                          borderLeft:`1px solid ${C.border}`}}>
                                          {cnt>0
                                            ? <span style={{fontWeight:700,fontSize:13,color:bcol}}>{cnt}</span>
                                            : <span style={{color:'#e2e8f0',fontSize:11}}>—</span>}
                                        </td>
                                        <td style={{padding:'9px 8px',textAlign:'center'}}>
                                          {cnt>0
                                            ? <span style={{fontSize:11,color:C.text3,fontWeight:600}}>
                                                {ctPct}%
                                              </span>
                                            : <span style={{color:'#e2e8f0',fontSize:11}}>—</span>}
                                        </td>
                                      </React.Fragment>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                            {/* Totals row */}
                            <tr style={{background:'#1B2D6B',borderTop:'2px solid #1B2D6B'}}>
                              <td style={{padding:'10px 16px',fontSize:12,fontWeight:800,
                                color:'#fff',position:'sticky',left:0,background:'#1B2D6B',
                                borderRight:'1px solid rgba(255,255,255,0.2)'}}>
                                TOTAL
                              </td>
                              <td style={{padding:'10px 12px',textAlign:'center',
                                borderLeft:'1px solid rgba(255,255,255,0.2)'}}>
                                <span style={{fontWeight:800,fontSize:15,color:'#fca5a5'}}>
                                  {grandTotal}
                                </span>
                              </td>
                              {buckets.map(b=>{
                                const total = monthData.reduce((s,r)=>{
                                  const rb=(r.buckets||{})[b];
                                  return s+(typeof rb==='object'&&rb!==null?(rb.count||0):(rb||0));
                                },0);
                                const pct   = grandTotal>0?Math.round((total/grandTotal)*100):0;
                                const bcol  = BUCKET_COLORS[b]||C.blue;
                                return (
                                  <React.Fragment key={b}>
                                    <td style={{padding:'10px 8px',textAlign:'center',
                                      borderLeft:'1px solid rgba(255,255,255,0.15)'}}>
                                      <span style={{fontWeight:800,fontSize:13,color:bcol}}>{total}</span>
                                    </td>
                                    <td style={{padding:'10px 8px',textAlign:'center'}}>
                                      <span style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,.6)'}}>{pct}%</span>
                                    </td>
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
              }
            </div>
          );
        })()}

        {/* ══ ROW 2 — COMPLIANCE ANALYTICS ═══════════════════ */}
        <div style={{marginBottom:24}}>
          {/* Header */}
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <span style={{fontSize:20}}>🛡️</span>
            <span style={{fontSize:15,fontWeight:700,color:C.text1}}>Compliance Analytics</span>
            <span style={{fontSize:11,color:C.text3,background:'#eaf1f8',padding:'2px 8px',borderRadius:20}}>
              TGP · Knowledge Transfer · Joint Call 
            </span>
            <div style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center'}}>
              <span style={{fontSize:11,color:C.text3}}>{compliance.length} records</span>
              <Button size="small" icon={<ReloadOutlined/>} loading={compLoading}
                onClick={loadCompliance} style={{borderRadius:8,fontSize:11}}>
                Refresh
              </Button>
            </div>
          </div>

          {compLoading && !compliance.length
            ? <div style={{textAlign:'center',padding:40}}><Spin/></div>
            : compliance.length===0
              ? <div style={{textAlign:'center',padding:40,color:C.text3,
                  background:C.white,borderRadius:14,border:`1px solid ${C.border}`}}>
                  No compliance data available
                </div>
              : (<>
                  {/* KPI chips */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:12,marginBottom:16}}>
                    {[
                      {icon:'📋',label:'Total Records',    value:compStats?.total,    color:C.navy,   bg:'#eff6ff'},
                      {icon:'✅',label:'TGP Compliance',    value:compStats?.tgpDone,
                        sub:`${compStats?.total?Math.round((compStats.tgpDone/compStats.total)*100):0}%`,
                        color:C.green,  bg:C.lgreen},
                      {icon:'📝',label:'Knowledge Transfer',  value:compStats?.ackDone,
                        sub:`${compStats?.total?Math.round((compStats.ackDone/compStats.total)*100):0}%`,
                        color:C.blue,   bg:C.lblue},
                      {icon:'🎓',label:'Joint Visit',   value:compStats?.indDone,
                        sub:`${compStats?.total?Math.round((compStats.indDone/compStats.total)*100):0}%`,
                        color:C.teal,   bg:C.lteal},
                      // {icon:'🏆',label:'Fully Compliant',  value:compStats?.allDone,
                      //   sub:`${compStats?.total?Math.round((compStats.allDone/compStats.total)*100):0}%`,
                      //   color:C.green,  bg:C.lgreen},
                      // {icon:'⚠️',label:'Non-Compliant',    value:compStats?.noneDone,
                      //   sub:`${compStats?.total?Math.round((compStats.noneDone/compStats.total)*100):0}%`,
                      //   color:C.red,    bg:C.lred},
                    ].map((c,i)=>(
                      <div key={i} style={{background:c.bg,borderRadius:14,
                        border:`1px solid ${c.color}22`,borderTop:`3px solid ${c.color}`,
                        padding:'14px 16px',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
                        <div style={{fontSize:22,marginBottom:6}}>{c.icon}</div>
                        <div style={{fontSize:26,fontWeight:800,color:c.color,lineHeight:1}}>
                          {c.value??'—'}
                        </div>
                        <div style={{fontSize:10,fontWeight:700,color:'#7a96b0',
                          textTransform:'uppercase',letterSpacing:'0.5px',marginTop:4}}>
                          {c.label}
                        </div>
                        {c.sub&&<div style={{fontSize:12,fontWeight:700,color:c.color,marginTop:2}}>{c.sub}</div>}
                      </div>
                    ))}
                  </div>

                  {/* Progress bars + Bar chart side by side */}
                  <div style={{display:'grid',gridTemplateColumns:'360px 1fr',gap:16,marginBottom:16}}>
                    {/* Progress bars */}
                    <Panel>
                      <Sec title="Overall Compliance Rate" sub="% of employees compliant"/>
                      {[
                        {label:'TGP Compliance',            done:compStats?.tgpDone, color:C.green},
                        {label:'knowledge Transfer', done:compStats?.ackDone, color:C.blue},
                        {label:'Joint Call Compliance',       done:compStats?.indDone, color:C.teal},
                        // {label:'Fully Compliant (all 3)',    done:compStats?.allDone, color:C.purple},
                      ].map(row=>{
                        const pct=compStats?.total?Math.round((row.done/compStats.total)*100):0;
                        return (
                          <div key={row.label} style={{marginBottom:14}}>
                            <div style={{display:'flex',justifyContent:'space-between',
                              fontSize:12,fontWeight:600,color:C.text2,marginBottom:4}}>
                              <span>{row.label}</span>
                              <span style={{fontWeight:800,color:row.color}}>
                                {pct}% <span style={{color:C.text3,fontWeight:400}}>({row.done}/{compStats?.total})</span>
                              </span>
                            </div>
                            <div style={{height:8,background:'#eaf1f8',borderRadius:20,overflow:'hidden'}}>
                              <div style={{height:'100%',width:`${pct}%`,background:row.color,
                                borderRadius:20,transition:'width .6s ease'}}/>
                            </div>
                          </div>
                        );
                      })}
                    </Panel>

                    {/* Bar chart — by designation */}
                    <Panel>
                      <Sec title="Compliance by Designation" sub="Top 10 designations"/>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={compStats?.byDesignation||[]}
                          margin={{top:4,right:8,left:-10,bottom:60}}>
                          <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                          <XAxis dataKey="designation" interval={0}
                            axisLine={false} tickLine={false} height={60}
                            tick={({x,y,payload})=>(
                              <g transform={`translate(${x},${y})`}>
                                <text x={0} y={0} dy={10} textAnchor="end"
                                  fill={C.text2} fontSize={10} fontWeight={600}
                                  transform="rotate(-32)">
                                  {payload.value.length>14?payload.value.slice(0,14)+'…':payload.value}
                                </text>
                              </g>
                            )}/>
                          <YAxis allowDecimals={false} tick={{fontSize:11,fill:C.text3}}
                            axisLine={false} tickLine={false}/>
                          <RTooltip content={<ChartTip/>}/>
                          <Legend wrapperStyle={{fontSize:11,paddingTop:8}}/>
                          <Bar dataKey="tgp" name="TGP"             fill={C.green}  radius={[3,3,0,0]} maxBarSize={16}/>
                          <Bar dataKey="ack" name="Knowledge Transfer" fill={C.blue}   radius={[3,3,0,0]} maxBarSize={16}/>
                          <Bar dataKey="ind" name="Joint Call"       fill={C.teal}   radius={[3,3,0,0]} maxBarSize={16}/>
                        </BarChart>
                      </ResponsiveContainer>
                    </Panel>
                  </div>

                  {/* Compliance Table
                  <div className="dash-table-wrap">
                    <div style={{padding:'12px 18px',
                      background:`linear-gradient(135deg,${C.navy},#2a4a9f)`,
                      display:'flex',justifyContent:'space-between',alignItems:'center',gap:10}}>
                      <span style={{fontWeight:700,fontSize:14,color:'#fff'}}>🛡️ Compliance Records</span>
                      <div style={{display:'flex',gap:10,alignItems:'center'}}>
                        <input placeholder="Search code / designation…"
                          value={compSearch}
                          onChange={e=>{setCompSearch(e.target.value);setCompPage(1);}}
                          style={{padding:'4px 12px',borderRadius:8,border:'none',
                            fontSize:12,width:200,outline:'none'}}/>
                        <span style={{fontSize:12,color:'rgba(255,255,255,.7)'}}>{compliance.length} records</span>
                      </div>
                    </div>
                    <Table size="small"
                      dataSource={compliance.filter(r=>
                        !compSearch||
                        (r.empCode||'').toLowerCase().includes(compSearch.toLowerCase())||
                        (r.designation||'').toLowerCase().includes(compSearch.toLowerCase())
                      )}
                      rowKey={(r,i)=>r.id??i}
                      loading={compLoading}
                      pagination={{current:compPage,pageSize:12,
                        onChange:p=>setCompPage(p),
                        showTotal:t=>`${t} records`,showSizeChanger:false}}
                      columns={[
                        {title:'#',width:44,render:(_,__,i)=>
                          <span style={{color:C.text3,fontSize:11}}>{i+1}</span>},
                        {title:'Emp Code',dataIndex:'empCode',width:110,
                          render:v=><span style={{fontFamily:'monospace',fontWeight:700,color:C.navy}}>{v||'—'}</span>},
                        {title:'Designation',dataIndex:'designation',width:150,ellipsis:true,
                          render:v=><span style={{fontSize:12,color:C.text2}}>{v||'—'}</span>},
                        {title:'TGP',dataIndex:'tgpCompliance',width:90,align:'center',
                          render:v=>{const y=(v||'').toLowerCase()==='yes';
                            return <Tag style={{borderRadius:20,fontWeight:700,fontSize:11,
                              background:y?C.lgreen:C.lred,color:y?C.green:C.red,
                              border:`1px solid ${y?'#86efac':'#fca5a5'}`}}>{y?'✅ Yes':'❌ No'}</Tag>;}},
                        {title:'Acknowledgement',dataIndex:'acknowledgementCompliance',width:140,align:'center',
                          render:v=>{const y=(v||'').toLowerCase()==='yes';
                            return <Tag style={{borderRadius:20,fontWeight:700,fontSize:11,
                              background:y?C.lblue:C.lred,color:y?C.blue:C.red,
                              border:`1px solid ${y?'#bfdbfe':'#fca5a5'}`}}>{y?'✅ Yes':'❌ No'}</Tag>;}},
                        {title:'Induction',dataIndex:'inductionCompliance',width:110,align:'center',
                          render:v=>{const y=(v||'').toLowerCase()==='yes';
                            return <Tag style={{borderRadius:20,fontWeight:700,fontSize:11,
                              background:y?C.lteal:C.lred,color:y?C.teal:C.red,
                              border:`1px solid ${y?'#67e8f9':'#fca5a5'}`}}>{y?'✅ Yes':'❌ No'}</Tag>;}},
                        {title:'Status',width:110,align:'center',render:(_,r)=>{
                          const t=(r.tgpCompliance||'').toLowerCase()==='yes';
                          const ak=(r.acknowledgementCompliance||'').toLowerCase()==='yes';
                          const ind=(r.inductionCompliance||'').toLowerCase()==='yes';
                          const all=t&&ak&&ind,none=!t&&!ak&&!ind;
                          return <Tag style={{borderRadius:20,fontWeight:700,fontSize:11,
                            background:all?C.lgreen:none?C.lred:C.lamber,
                            color:all?C.green:none?C.red:C.amber,
                            border:`1px solid ${all?'#86efac':none?'#fca5a5':'#fde68a'}`}}>
                            {all?'🏆 Full':none?'⚠️ None':'🔄 Partial'}</Tag>;}},
                        {title:'Created',dataIndex:'createdDate',width:100,
                          render:v=>v?<span style={{fontSize:11,color:C.text3}}>{dayjs(v).format('DD MMM YY')}</span>:'—'},
                        {title:'Updated',dataIndex:'modifiedDate',width:100,
                          render:v=>v?<span style={{fontSize:11,color:C.text3}}>{dayjs(v).format('DD MMM YY')}</span>:'—'},
                      ]}/>
                  </div> */}
                </>)
          }
        </div>


        {/* ══ CALL PERFORMANCE KPIs + Q&A FEEDBACK ═══════════════ */}
         <div style={{marginBottom:24}}>
          {/* Header */}
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <span style={{fontSize:15,fontWeight:700,color:C.text1}}>Connect Compliance</span>
            
            {/* <div style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center'}}>
              <span style={{fontSize:11,color:C.text3}}>{compliance.length} records</span>
              <Button size="small" icon={<ReloadOutlined/>} loading={compLoading}
                onClick={loadCompliance} style={{borderRadius:8,fontSize:11}}>
                Refresh
              </Button>
            </div> */}
          </div>
          </div>
        <div style={{marginBottom:24}}>

          {/* ── 4 Call KPI chips ── */}
          {(() => {
            const buildDrill = (type) => {
              const active = employees.filter(e => !e.isResigned);
              const enrich = e => {
                const agingDays = getAgingDays(e);
                const dueKeys   = CALL_KEYS.filter(k => CALL_DAYS[k] <= agingDays);
                const completed = dueKeys.filter(k => e.calls?.find(c => c.callKey===k && c.status==='Completed')).length;
                const pending   = dueKeys.filter(k => {
                  const c = e.calls?.find(x => x.callKey===k);
                  if (!c) return true;
                  return !['Completed','NA','No Response'].includes(c.status);
                }).length;
                return { ...e, _due: dueKeys.length, _completed: completed, _pending: pending };
              };
              if (type === 'targeted') return active.filter(e => !isLegacy(e) && CALL_KEYS.some(k => CALL_DAYS[k] <= getAgingDays(e))).map(enrich);
              if (type === 'completed') return active.filter(e => !isLegacy(e) && CALL_KEYS.some(k => CALL_DAYS[k] <= getAgingDays(e) && e.calls?.find(c => c.callKey===k && c.status==='Completed'))).map(enrich);
              if (type === 'pending')   return active.filter(e => {
                if (isLegacy(e)) return false; // pre-Dec'25 always excluded first
                const doj = e.dateOfJoining || e.cohortDate;
                if (!doj) return false;
                const agingDays = dayjs().diff(dayjs(doj), 'day');
                if (agingDays >= 180) {
                  // Journey-closed: only show if explicitly Missed
                  return CALL_KEYS.some(k => {
                    const c = e.calls?.find(x => x.callKey===k);
                    return c && c.status === 'Missed';
                  });
                }
                return CALL_KEYS.some(k => {
                  if (CALL_DAYS[k] > agingDays) return false;
                  const c = e.calls?.find(x => x.callKey===k);
                  if (c) return c.status === 'Missed';
                  return dayjs().diff(dayjs(doj).add(CALL_DAYS[k], 'day'), 'day') >= 1;
                });
              }).map(enrich);
              return [];
            };
            const cards = [
              { icon:'🎯', label:'Total Targeted Calls',
                value: a ? (a.callFunnel||[]).reduce((s,cf)=>s+cf.due,0) : null,
                sub:'Calls due across all stages',
                color:C.navy, bg:'#eff6ff', type:'targeted' },
              { icon:'✅', label:'Total Calls Completed',
                value: a?.allCounts.completed,
                sub:'Successfully logged',
                color:C.green, bg:C.lgreen, type:'completed' },
              { icon:'⏳', label:'Total Calls Pending',
                value: a?.pendingEmpsCount,
                sub: a?.pendingEmpsCount>0 ? '⚠️ Action required' : '✅ All clear',
                color:a?.pendingEmpsCount>0?C.amber:C.green,
                bg:a?.pendingEmpsCount>0?C.lamber:C.lgreen, type:'pending',
                empLink: '/employees?tab=pending_calls' },
              { icon:'📶', label:'Connectivity %',
                value: a?`${a.connectivity}%`:null,
                sub: a?.connectivity>=70?'🟢 Healthy target':'🟡 Needs improvement',
                color:a?.connectivity>=70?C.green:C.amber,
                bg:a?.connectivity>=70?C.lgreen:C.lamber, type:null },
            ];
            return (
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:20}}>
                {cards.map((k,i)=>(
                  <div key={i} style={{
                    background:k.bg, borderRadius:14,
                    border:`1px solid ${k.color}22`,
                    borderTop:`3px solid ${k.color}`,
                    padding:'18px 16px',
                    boxShadow:'0 1px 4px rgba(0,0,0,0.05)',
                    transition:'transform .18s, box-shadow .18s',
                    cursor: k.type ? 'pointer' : 'default',
                  }}
                  onClick={() => k.type && setCompDrill({ title: k.label, type: k.type, emps: buildDrill(k.type) })}
                  onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; if(k.type) e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.10)'; }}
                  onMouseLeave={e=>{ e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.05)'; }}>
                    <div style={{fontSize:26,marginBottom:8}}>{k.icon}</div>
                    {loading
                      ? <div style={{height:30,background:`${k.color}22`,borderRadius:6,marginBottom:8}}/>
                      : <div style={{fontSize:28,fontWeight:800,color:k.color,lineHeight:1,marginBottom:5,letterSpacing:'-1px',
                          ...(k.type && { textDecoration:'underline dotted', textUnderlineOffset:3 })}}>
                          {k.value??'—'}
                        </div>
                    }
                    <div style={{fontSize:11,fontWeight:700,color:'#7a96b0',
                      textTransform:'uppercase',letterSpacing:'0.6px',marginBottom:2}}>
                      {k.label}
                    </div>
                    <div style={{fontSize:11,color:k.color,fontWeight:600}}>{k.sub}</div>
                    {k.empLink && k.type === 'pending' && (
                      <div
                        onClick={e => { e.stopPropagation(); nav(k.empLink); }}
                        style={{
                          marginTop:8, display:'inline-flex', alignItems:'center', gap:4,
                          fontSize:11, fontWeight:700, color:C.amber,
                          cursor:'pointer', textDecoration:'underline dotted',
                          textUnderlineOffset:2,
                        }}>
                        👥 View employees →
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* ── Q&A Feedback Question Cards ── */}
          <FeedbackQuestionCards/>
        </div>


       

        {/* ══ INBOUND CALL ANALYTICS ════════════════════════════ */}
        <div style={{marginBottom:24}}>

          {/* ── Section header ── */}
          <div style={{display:'flex',alignItems:'center',gap:10,
            marginBottom:16,flexWrap:'wrap'}}>
            <span style={{fontSize:20}}>📲</span>
            <span style={{fontSize:15,fontWeight:700,color:C.text1}}>
              Inbound Call Analytics
            </span>
            <span style={{fontSize:11,color:C.text3,background:'#eaf1f8',
              padding:'2px 8px',borderRadius:20}}>
              Employee-initiated support queries
            </span>
            <div style={{marginLeft:'auto'}}>
              <Button size="small" icon={<ReloadOutlined/>}
                loading={inboundLoading} onClick={loadInbound}
                style={{borderRadius:8,fontSize:11}}>
                Refresh
              </Button>
            </div>
          </div>

          {inboundLoading && !inboundStats
            ? <div style={{textAlign:'center',padding:32}}><Spin/></div>
            : !inboundStats
              ? <div style={{textAlign:'center',padding:28,color:C.text3,
                  background:C.white,borderRadius:14,
                  border:`1px solid ${C.border}`}}>
                  No inbound data available
                </div>
              : (<>
                  {/* ── 3 KPI Cards ── */}
                  <div style={{display:'grid',
                    gridTemplateColumns:'repeat(3,1fr)',
                    gap:14,marginBottom:16}}>
                    {[
                      { icon:'📋', label:'Total Queries',
                        value: inboundStats.total,
                        sub:'All logged queries',
                        color:C.navy, bg:'#eff6ff' },
                      { icon:'⏳', label:'Open Queries',
                        value: inboundStats.open,
                        sub: inboundStats.open > 0
                          ? '⚠️ Awaiting resolution' : '✅ All resolved',
                        color: inboundStats.open > 0 ? C.amber : C.green,
                        bg:    inboundStats.open > 0 ? C.lamber : C.lgreen },
                      { icon:'✅', label:'Closed Queries',
                        value: inboundStats.closed,
                        sub: inboundStats.total > 0
                          ? `${Math.round((inboundStats.closed/inboundStats.total)*100)}% resolution rate`
                          : '—',
                        color:C.green, bg:C.lgreen },
                    ].map((k,i) => (
                      <div key={i}
                        onClick={() => nav('/inbound')}
                        style={{
                          background:k.bg, borderRadius:14,
                          border:`1px solid ${k.color}22`,
                          borderTop:`3px solid ${k.color}`,
                          padding:'18px 20px', cursor:'pointer',
                          boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
                          display:'flex', alignItems:'center', gap:16,
                          transition:'transform .18s, box-shadow .18s',
                        }}
                        onMouseEnter={e=>{
                          e.currentTarget.style.transform='translateY(-2px)';
                          e.currentTarget.style.boxShadow=`0 8px 24px ${k.color}22`;
                        }}
                        onMouseLeave={e=>{
                          e.currentTarget.style.transform='';
                          e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.04)';
                        }}>
                        <div style={{fontSize:36,lineHeight:1}}>{k.icon}</div>
                        <div>
                          <div style={{fontSize:32,fontWeight:900,color:k.color,
                            lineHeight:1,marginBottom:4,letterSpacing:'-1px'}}>
                            {k.value ?? '—'}
                          </div>
                          <div style={{fontSize:11,fontWeight:700,color:'#7a96b0',
                            textTransform:'uppercase',letterSpacing:'0.6px',marginBottom:2}}>
                            {k.label}
                          </div>
                          <div style={{fontSize:11,color:k.color,fontWeight:600}}>
                            {k.sub}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ── Query type breakdown table ── */}
                  {(inboundStats.byType||[]).length > 0 && (
                    <div style={{background:C.white,borderRadius:14,
                      border:`1px solid ${C.border}`,
                      boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
                      overflow:'hidden'}}>

                      {/* Table header */}
                      <div style={{
                        padding:'12px 20px',
                        background:`linear-gradient(135deg,${C.navy},#2a4a9f)`,
                        display:'flex',alignItems:'center',
                        justifyContent:'space-between',gap:10,
                      }}>
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <span style={{fontSize:14,fontWeight:700,color:'#fff'}}>
                            📊 Query Type Breakdown
                          </span>
                          <span style={{fontSize:11,
                            color:'rgba(255,255,255,.55)'}}>
                            {inboundStats.total} total queries
                          </span>
                        </div>
                        <Button size="small"
                          onClick={() => nav('/inbound')}
                          style={{borderRadius:8,
                            background:'rgba(255,255,255,0.15)',
                            border:'1px solid rgba(255,255,255,0.3)',
                            color:'#fff',fontSize:11,fontWeight:600}}>
                          View All →
                        </Button>
                      </div>

                      {/* Column headers */}
                      <div style={{
                        display:'grid',
                        gridTemplateColumns:'1fr 72px 72px 140px',
                        gap:10,padding:'8px 20px',
                        background:C.surf,
                        borderBottom:`1px solid ${C.border}`,
                      }}>
                        {['Query Type','Total','Open','Resolution'].map(h=>(
                          <div key={h} style={{fontSize:10,fontWeight:700,
                            color:C.text3,textTransform:'uppercase',
                            letterSpacing:'0.5px'}}>
                            {h}
                          </div>
                        ))}
                      </div>

                      {/* Data rows */}
                      <div>
                        {(inboundStats.byType||[]).map((t,i)=>{
                          const closed  = (t.count||0)-(t.open||0);
                          const resPct  = t.count>0
                            ? Math.round((closed/t.count)*100) : 0;
                          const col     = resPct===100 ? C.green
                            : resPct>=50 ? C.blue : C.amber;
                          const colBg   = resPct===100 ? C.lgreen
                            : resPct>=50 ? C.lblue : C.lamber;
                          const ICONS   = {
                            ipad_accessories:'📱',
                            company_systems:'💻',
                            expense_reimbursement:'💰',
                            mediclaim_policy:'🏥',
                            esim_activation:'📡',
                            input_items:'📦',
                            others:'❓',
                          };
                          const isLast = i===(inboundStats.byType.length-1);
                          return (
                            <div key={t.key||i} style={{
                              display:'grid',
                              gridTemplateColumns:'1fr 72px 72px 140px',
                              gap:10, alignItems:'center',
                              padding:'11px 20px',
                              background:i%2===0 ? C.white : '#fafcff',
                              borderBottom: isLast
                                ? 'none' : `1px solid ${C.border}`,
                              transition:'background .1s',
                            }}
                            onMouseEnter={e=>e.currentTarget.style.background='#eff6ff'}
                            onMouseLeave={e=>e.currentTarget.style.background=i%2===0?C.white:'#fafcff'}>

                              {/* Label */}
                              <div style={{display:'flex',alignItems:'center',
                                gap:10,overflow:'hidden'}}>
                                <span style={{fontSize:18,flexShrink:0}}>
                                  {ICONS[t.key]||'❓'}
                                </span>
                                <span style={{fontSize:13,fontWeight:600,
                                  color:C.text1,overflow:'hidden',
                                  textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                  {t.label}
                                </span>
                              </div>

                              {/* Total */}
                              <div style={{textAlign:'center',
                                fontSize:15,fontWeight:800,color:C.text1}}>
                                {t.count||0}
                              </div>

                              {/* Open */}
                              <div style={{textAlign:'center'}}>
                                {(t.open||0)>0
                                  ? <span style={{
                                      fontSize:13,fontWeight:800,
                                      color:C.amber,background:C.lamber,
                                      padding:'2px 10px',borderRadius:20,
                                      border:`1px solid ${C.amber}44`,
                                    }}>
                                      {t.open}
                                    </span>
                                  : <span style={{fontSize:11,
                                      color:C.text3}}>—</span>}
                              </div>

                              {/* Resolution bar */}
                              <div style={{display:'flex',
                                alignItems:'center',gap:8}}>
                                <div style={{flex:1,height:7,
                                  background:'#eaf1f8',borderRadius:20,
                                  overflow:'hidden'}}>
                                  <div style={{
                                    height:'100%',
                                    width:`${resPct}%`,
                                    background:col,
                                    borderRadius:20,
                                    transition:'width .6s ease',
                                  }}/>
                                </div>
                                <span style={{
                                  fontSize:11,fontWeight:800,
                                  color:col,minWidth:36,
                                  textAlign:'right',
                                }}>
                                  {resPct}%
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Footer */}
                      <div style={{
                        padding:'10px 20px',
                        borderTop:`1px solid ${C.border}`,
                        background:C.surf,
                        display:'flex',
                        justifyContent:'space-between',
                        alignItems:'center',
                      }}>
                        <span style={{fontSize:11,color:C.text3}}>
                          {inboundStats.open||0} open ·{' '}
                          {inboundStats.closed||0} resolved ·{' '}
                          {inboundStats.total>0
                            ? Math.round((inboundStats.closed/inboundStats.total)*100)
                            : 0}% overall resolution
                        </span>
                        <Button size="small" type="link"
                          onClick={() => nav('/inbound')}
                          style={{color:C.blue,fontWeight:600,
                            fontSize:12,padding:0}}>
                          View All Inbound Queries →
                        </Button>
                      </div>
                    </div>
                  )}
                </>)
          }
        </div>

      </div>

      {/* ══ CONNECT COMPLIANCE DRILL-DOWN MODAL ══════════════ */}
      <Modal
        title={
          <span style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>
            {compDrill?.type === 'targeted'  && '🎯 '}
            {compDrill?.type === 'completed' && '✅ '}
            {compDrill?.type === 'pending'   && '⏳ '}
            {compDrill?.title}
            {compDrill && <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
              — {compDrill.emps.length} employee{compDrill.emps.length !== 1 ? 's' : ''}
            </span>}
          </span>
        }
        open={!!compDrill}
        onCancel={() => { setCompDrill(null); setCompDrillSearch(''); setCompDrillSort({ key: null, dir: 'asc' }); }}
        footer={null}
        width={900}
        styles={{ body: { padding: '12px 0 0' } }}
      >
        {compDrill && (() => {
          const isPending   = compDrill.type === 'pending';
          const isCompleted = compDrill.type === 'completed';

          // ── Search filter ──
          const q = compDrillSearch.trim().toLowerCase();
          const filtered = compDrill.emps.filter(e =>
            !q ||
            (e.empCode  || '').toLowerCase().includes(q) ||
            (e.empName  || e.name || '').toLowerCase().includes(q) ||
            (e.division || '').toLowerCase().includes(q)
          ).map(e => ({ ...e, _tenure: e.dateOfJoining ? dayjs().diff(dayjs(e.dateOfJoining), 'day') : null }));

          // ── Sort ──
          const { key: sKey, dir: sDir } = compDrillSort;
          const sorted = sKey ? [...filtered].sort((a, b) => {
            let av, bv;
            if (sKey === 'code')    { av = a.empCode  || ''; bv = b.empCode  || ''; }
            if (sKey === 'name')    { av = a.empName  || a.name || ''; bv = b.empName  || b.name || ''; }
            if (sKey === 'div')     { av = a.division || ''; bv = b.division || ''; }
            if (sKey === 'cohort')  { av = a.cohortName || ''; bv = b.cohortName || ''; }
            if (sKey === 'doj')     { av = a.dateOfJoining || ''; bv = b.dateOfJoining || ''; }
            if (sKey === 'tenure')  { av = a._tenure ?? -1; bv = b._tenure ?? -1; }
            if (sKey === 'due')     { av = a._due;  bv = b._due; }
            if (sKey === 'comp')    { av = a._completed; bv = b._completed; }
            if (sKey === 'pend')    { av = a._pending;   bv = b._pending; }
            const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
            return sDir === 'asc' ? cmp : -cmp;
          }) : filtered;

          const toggleSort = (k) => setCompDrillSort(prev =>
            prev.key === k ? { key: k, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' }
          );
          const SortIcon = ({ k }) => sKey === k
            ? <span style={{ marginLeft: 4, fontSize: 10 }}>{sDir === 'asc' ? '▲' : '▼'}</span>
            : <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.35 }}>⇅</span>;

          const cols = [
            { label: '#',             key: null,      align: 'center' },
            { label: 'Code',          key: 'code',    align: 'center' },
            { label: 'Name',          key: 'name',    align: 'left'   },
            { label: 'Division',      key: 'div',     align: 'left'   },
            { label: 'Cohort Name',   key: 'cohort',  align: 'left'   },
            { label: 'Join Date',     key: 'doj',     align: 'center' },
            { label: 'Tenure (days)', key: 'tenure',  align: 'center' },
            { label: 'Due',           key: 'due',     align: 'center' },
            { label: 'Completed',     key: 'comp',    align: 'center' },
            { label: 'Pending',       key: 'pend',    align: 'center' },
          ];

          const exportToExcel = () => {
              const rows = sorted.map((e, i) => ({
                '#':              i + 1,
                'Emp Code':       e.empCode    || '',
                'Name':           e.empName    || e.name || '',
                'Division':       e.division   || '',
                'Cohort Name':    e.cohortName || '',
                'Join Date':      e.dateOfJoining ? dayjs(e.dateOfJoining).format('DD MMM YYYY') : '',
                'Tenure (days)':  e._tenure    ?? '',
                'Due':            e._due,
                'Completed':      e._completed,
                'Pending':        e._pending,
              }));
              const ws = XLSX.utils.json_to_sheet(rows);
              ws['!cols'] = [4,12,22,18,16,14,14,6,10,8].map(w => ({ wch: w }));
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, compDrill.title.slice(0, 31));
              XLSX.writeFile(wb, `${compDrill.title.replace(/[^a-zA-Z0-9]/g,'_')}_${dayjs().format('DDMMMYYYY')}.xlsx`);
            };

          return (
            <>
              {/* Search bar */}
              <div style={{ padding: '0 16px 10px', display: 'flex', gap: 10, alignItems: 'center' }}>
                <input
                  value={compDrillSearch}
                  onChange={e => setCompDrillSearch(e.target.value)}
                  placeholder="Search by code, name or division…"
                  style={{
                    flex: 1, padding: '7px 12px', borderRadius: 8, fontSize: 12,
                    border: `1px solid ${C.border}`, outline: 'none', color: C.text1,
                  }}
                />
                {compDrillSearch && (
                  <button onClick={() => setCompDrillSearch('')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: C.text3, padding: '0 4px' }}>
                    ✕
                  </button>
                )}
                <span style={{ fontSize: 12, color: C.text3, whiteSpace: 'nowrap' }}>
                  {sorted.length} / {compDrill.emps.length}
                </span>
                <button
                  onClick={exportToExcel}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', border: '1.5px solid #16a34a',
                    background: '#f0fdf4', color: '#16a34a', whiteSpace: 'nowrap',
                  }}>
                  ⬇ Export Excel
                </button>
              </div>

              {/* Table */}
              <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                    <tr style={{ background: C.navy }}>
                      {cols.map(c => (
                        <th key={c.label}
                          onClick={() => c.key && toggleSort(c.key)}
                          style={{
                            padding: '9px 10px', color: '#fff', fontWeight: 700, whiteSpace: 'nowrap',
                            textAlign: c.align, borderRight: '1px solid rgba(255,255,255,0.15)',
                            cursor: c.key ? 'pointer' : 'default',
                            userSelect: 'none',
                            background: sKey === c.key ? '#1e3a6e' : C.navy,
                          }}>
                          {c.label}{c.key && <SortIcon k={c.key} />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.length === 0
                      ? <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No employees found</td></tr>
                      : sorted.map((e, i) => {
                          const rowBg = isPending && e._pending > 0 ? '#fff7ed'
                                      : isCompleted && e._completed > 0 ? '#f0fdf4'
                                      : i % 2 === 0 ? '#f9fafb' : '#fff';
                          return (
                            <tr key={e.empCode || i} style={{ background: rowBg, borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '8px 10px', textAlign: 'center', color: '#9ca3af', fontWeight: 600 }}>{i + 1}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: C.navy }}>{e.empCode || '—'}</td>
                              <td style={{ padding: '8px 10px', fontWeight: 600, color: '#111827' }}>{e.empName || e.name || '—'}</td>
                              <td style={{ padding: '8px 10px', color: '#4b5563' }}>{e.division || '—'}</td>
                              <td style={{ padding: '8px 10px', color: '#4b5563', whiteSpace: 'nowrap' }}>{e.cohortName || '—'}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', color: '#6b7280' }}>
                                {e.dateOfJoining ? dayjs(e.dateOfJoining).format('DD MMM YYYY') : '—'}
                              </td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 800,
                                color: e._tenure === null ? '#9ca3af' : e._tenure <= 30 ? C.green : e._tenure <= 90 ? C.amber : C.navy }}>
                                {e._tenure !== null ? e._tenure : '—'}
                              </td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: C.navy }}>{e._due}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 800,
                                color: e._completed === e._due ? C.green : e._completed > 0 ? C.amber : '#9ca3af' }}>
                                {e._completed}
                              </td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 800,
                                color: e._pending > 0 ? C.red : C.green }}>
                                {e._pending > 0 ? e._pending : '✓'}
                              </td>
                            </tr>
                          );
                        })
                    }
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div style={{ padding: '10px 16px', background: '#f3f4f6', borderTop: '1px solid #e5e7eb',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  Showing <strong>{sorted.length}</strong> of <strong>{compDrill.emps.length}</strong> employee{compDrill.emps.length !== 1 ? 's' : ''}
                </span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  Click any column header to sort · Tenure: green ≤30d · amber ≤90d
                </span>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* ══ 180-DAY COMPLETED JOURNEY MODAL ═══════════════════ */}
      <Modal
        open={show180Modal}
        onCancel={() => { setShow180Modal(false); setModal180Search(''); }}
        footer={null}
        width={820}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>🎓</span>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#7c3aed' }}>
                Completed 180-Day Journey
              </div>
              <div style={{ fontSize: 12, fontWeight: 400, color: '#6b7280', marginTop: 1 }}>
                Dec '25 onwards · active employees with 180+ days tenure
              </div>
            </div>
            <div style={{ marginLeft: 'auto', background: '#f5f3ff',
              border: '1.5px solid #7c3aed', borderRadius: 8,
              padding: '4px 12px', fontSize: 22, fontWeight: 900, color: '#7c3aed' }}>
              {decPlusStats?.completed180 ?? 0}
            </div>
          </div>
        }
      >
        <Input.Search
          placeholder="Search by Emp Code, Name, Date of Joining…"
          allowClear
          value={modal180Search}
          onChange={e => setModal180Search(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        {(() => {
          const q = modal180Search.trim().toLowerCase();
          const list180 = decPlusStats?.completed180List ?? [];
          const filtered180 = q
            ? list180.filter(e =>
                (e.empCode || '').toLowerCase().includes(q) ||
                (e.empName || '').toLowerCase().includes(q) ||
                (() => { const doj = e.dateOfJoining || e.cohortDate; return doj && dayjs(doj).format('DD MMM YYYY').toLowerCase().includes(q); })()
              )
            : list180;
          return (
        <Table
          size="small"
          dataSource={filtered180}
          rowKey={r => `${r.empCode}-${r.dateOfJoining}`}
          pagination={{ pageSize: 10, showSizeChanger: false,
            showTotal: (t, [s, e]) => `${s}–${e} of ${t} employees` }}
          columns={[
            { title: 'Emp Code', dataIndex: 'empCode', width: 90,
              render: v => <span style={{ fontWeight: 700, color: '#7c3aed', fontSize: 12 }}>{v||'—'}</span> },
            { title: 'Name', dataIndex: 'empName', ellipsis: true,
              render: v => <span style={{ fontWeight: 600, fontSize: 13 }}>{v||'—'}</span> },
            { title: 'Division', dataIndex: 'division', width: 120,
              render: v => v ? <Tag style={{ borderRadius: 6, fontSize: 11 }}>{v}</Tag> : '—' },
            { title: 'Date of Joining', dataIndex: 'dateOfJoining', width: 130,
              render: v => v ? dayjs(v).format('DD MMM YYYY') : '—',
              sorter: (a, b) => dayjs(a.dateOfJoining||a.cohortDate).valueOf() - dayjs(b.dateOfJoining||b.cohortDate).valueOf(),
              defaultSortOrder: 'ascend' },
            { title: 'Days Completed', width: 130, align: 'center',
              render: (_, r) => {
                const doj = r.dateOfJoining || r.cohortDate;
                const days = doj ? dayjs().diff(dayjs(doj), 'day') : null;
                return days !== null
                  ? <Tag color="purple" style={{ borderRadius: 6, fontWeight: 700, fontSize: 12 }}>{days}d</Tag>
                  : '—';
              },
              sorter: (a, b) => {
                const da = dayjs().diff(dayjs(a.dateOfJoining||a.cohortDate), 'day');
                const db = dayjs().diff(dayjs(b.dateOfJoining||b.cohortDate), 'day');
                return da - db;
              }},
            { title: 'Status', width: 100, align: 'center',
              render: () => <Tag color="success" style={{ borderRadius: 6, fontWeight: 700 }}>✅ Active</Tag> },
          ]}
          style={{ marginTop: 8 }}
        />
          );
        })()}
      </Modal>

      <style>{`
        .ant-table-thead > tr > th {
          background: ${C.surf} !important;
          color: ${C.text2} !important;
          font-size: 11px !important; font-weight: 700 !important;
          letter-spacing: 0.5px !important; text-transform: uppercase !important;
          border-bottom: 1px solid ${C.border} !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid ${C.border}88 !important;
          padding: 10px 12px !important;
        }
        .ant-table-tbody > tr:hover > td { background: #f8faff !important; }
        .ant-card-head { border-bottom: none !important; }
      `}</style>
    </div>
  );
}